import * as d3 from "d3";
import { group as d3Group } from "d3-array";
import { DataUtilLocalStorage } from "../../data/util/LocalStorage";
import _L from "../../util/Labels";
import { CheckboxComponent } from "../components-lit/Checkbox/Checkbox";
import { HTMLFileIcoElement } from "../controlWC/FileIcoComponent";
import { HTMLIconCollapseElement } from "../controlWC/IconTogglerCollapseComponent";
import { HTMLSpinnerElement } from "../controlWC/SpinnerComponent";
import { UIUtilElementBehaviors } from "../util/ElementBehaviors";
import { UIUtilFormat } from "../util/Format";
import { UIUtilGlobalKeyEvents } from "../util/GlobalKeyEvent";
import { UIUtilIconResources } from "../util/IconResourses";
import { UIUtilLang } from "../util/Language";
import { UIUtilTime } from "../util/Time";
import { UIUtilGeneral } from "../util/Util";
import { Button } from "./Button";
import { FilterControlV2, IMainFilterItem, IMainFilterItemTypeConfig, TMainFilterType } from "./FilterControlV2";
import { FloatingOptionsBtns, TItemsFloatingOptionsCfg } from "./FloatingOptions";
import { MenuFlex } from "./MenuFlexV2";
import { TableSortArrowsComponent } from "./Tabla/components/TableSortArrows";
import { TableDataBuilder } from "./Tabla/core/TableDataBuilder";
import { TableSelectionManager } from "./Tabla/core/TableSelectionManager";
import { TableNode, TTableIdType } from "./Tabla/models/TableNode";
import { TableUtilsGeneral } from "./Tabla/utils/General";
import { CTableOriginEventType, CTableStatusOrder, ITableActiveSelectedDataAdvanced, ITableConfig, ITableMenuDataSelectedConfig as ITableMenuDataSelectedConfig_, ITableMenuTopDefaultConfig as ITableMenuTopDefaultConfig_, ITableParametroFiltroMap, ITableRowMultilineConfig, ITableStepEvaluator, ITableStepSubLevelEvaluator, TTableColumn, TTableColumnConfigV2, TTableMenuDataSelectedOptionConfig, TTableMenuTopDefaultOptionConfig, TTableParametroFiltro } from "./TablaTypes";
import { TableUtil as Util } from "./TablaUtil";
import { TableReorderer } from "./TablePlugins/TableReorderer";
import { TableResizer } from "./TablePlugins/TableResizer";

type TStepJoin = UIUtilGeneral.TTypeStepJoin;

let idCount = 0;
const PREFIX_IDENTIFIER = "tbl";
export namespace Table {
    const ANIMATION_DURATION = 400;

    export import CStatusOrder = CTableStatusOrder;
    export type IColumn<TDataTable> = TTableColumn<TDataTable>;
    export type IConfig<TDataTable> = ITableConfig<TDataTable>;
    export type IActiveSelectedDataAdvanced<TData, TData2 = any, TData3 = any, TData4 = any> = ITableActiveSelectedDataAdvanced<TData, TData2, TData3, TData4>;
    export type IParametroFiltro<TData = Object> = TTableParametroFiltro<TData>;
    export type IRowMultilineConfig<TParentData, TData> = ITableRowMultilineConfig<TParentData, TData>;
    export type IStepEvaluator<TData, TSubData1, TSubData2> = ITableStepEvaluator<TData, TSubData1, TSubData2>;
    export type ITableMenuDataSelectedConfig<T> = ITableMenuDataSelectedConfig_<T>;
    export type ITableMenuDataSelectedOptionConfig<TData = Object> = TTableMenuDataSelectedOptionConfig<TData>;
    export type ITableMenuTopDefaultConfig = ITableMenuTopDefaultConfig_;
    export type ITableMenuTopDefaultOptionConfig = TTableMenuTopDefaultOptionConfig;
    export type IStepSubLevelEvaluator<TData, TSubData1, TSubData2> = ITableStepSubLevelEvaluator<TData, TSubData1, TSubData2>;

    interface IPropieties<TData> {
        /** Datos Originales (INMUTABLE) */
        DataOrigin: Array<TData>;

        /** Datos Originales procesados por el control (Solo se procesan una vez por Update) */
        DataOriginTableMap: Map<TTableIdType, TableNode<TData>>;
        /** Datos Originales procesados por el control (Solo se procesan una vez por Update) */
        DataOriginTableArray: Array<TableNode<TData>>;

        /** Estructura de datos usados por la Tabla, son los datos que se mapean al DOM
         * * Incluye datos:
         *      - Checkeados
         *      - Inhabilitados
        */
        DataTableFilteredMap: Map<TTableIdType, TableNode<TData>>;
        /** Estructura de datos usados por la Tabla, son los datos que se mappean al DOM
         * * Incluye datos:
         *      - Checkeados
         *      - Inhabilitados
        */
        DataTableFilteredArray: Array<TableNode<TData>>;

        /** Propiedades de IConfig más propiedades de funcionamiento de la tabla */
        ColumnHeaders: Array<ITableField<TData>>
        /** Datos actuales confiables por Identificadores no repetidos
         * * En caso de ser falso, la próxima actualización de datos receteará los estados referentes a checkeo y collapso */
        ReliableCurrentData: boolean
    }

    type ILevelEvaluator = {
        Level: number;
    } & ITableStepSubLevelEvaluator<any, any, any>

    interface IControlesGenerales<TData> {
        /** Contenedor del control */
        DivContent?: d3.Selection<HTMLDivElement, any, HTMLElement, null>;

        /** Contenedor principal del menú superior <div class"area_acciones""> */
        A_DivAcciones: d3.Selection<HTMLDivElement, any, HTMLElement, null>;
        /** Contenedor principal del area de filtros <div class"area_filtros""> */
        A2_DivFiltros: d3.Selection<HTMLDivElement, any, HTMLElement, null>;
        /** Contenedor principal de la maquetación de la tabla * <div class"area_control""> */
        B_DivControl: d3.Selection<HTMLDivElement, any, HTMLElement, null>;

        AA_DivMenuOfChecks: d3.Selection<HTMLDivElement, any, HTMLElement, null>
        /** Contenedor directo de la tabla && area de scroll * <div class"space_tabla""> */
        BA_DivSpaceTabla: d3.Selection<HTMLDivElement, any, HTMLElement, null>;
        /** Tabla * <div class"control_tabla""> */
        BAAA_TablaHead: d3.Selection<HTMLDivElement, any, HTMLElement, null>;
        /** Contenedor de registros  * <div class"rbodyB""> -- <div> */
        BAAB_TablaBodyA: d3.Selection<HTMLDivElement, any, HTMLElement, null>;
        /** Contenedor de registros  * <div class"rbodyB""> -- <div> */
        BAAB_TablaBodyB: d3.Selection<HTMLDivElement, any, HTMLElement, null>;

        C_CtrlMenuInRowV2: MenuFlex & {
            __idData?: string;
            __data?: TableNode<any>;
            __onparentchanged?: (oldParent: TSelectionHTML<"htmlelement">, newParent: TSelectionHTML<"htmlelement">) => void;
            __onshow?: () => void;
            __onhide?: () => void;
        };
        C_CtrlMenuTopOfActiveSelectedV2: MenuFlex & {
            __onparentchanged?: (oldParent: TSelectionHTML<"htmlelement">, newParent: TSelectionHTML<"htmlelement">) => void;
            __onshow?: () => void;
            __onhide?: () => void;
        };
        C_CtrlMenuTopDefaultV2: MenuFlex & {
            __onparentchanged?: (oldParent: TSelectionHTML<"htmlelement">, newParent: TSelectionHTML<"htmlelement">) => void;
            __onshow?: () => void;
            __onhide?: () => void;
        };
        C_CtrlFiltrosV2: FilterControlV2;

        /** Control Check General por página */
        C_CheckCol: CheckboxComponent;
        /** Contenedor del espacio de pie */
        BB_DivSpacePie: d3.Selection<HTMLDivElement, {}, HTMLElement, null>;

        BBAA_DivButtonItPag: d3.Selection<HTMLDivElement, {}, HTMLElement, null>;

        BA_SpaceTablaStyleSizing: d3.Selection<HTMLStyleElement, {}, HTMLElement, null>;
        BA_SpaceTablaSwitchLastCellSizing: d3.Selection<HTMLStyleElement, {}, HTMLElement, null>;
    }

    type TInfoColumns<TData> = Omit<IColumn<TData>, keyof Pick<IColumn<TData>, "IsSortable" | "IsStickable">>[]

    export type ITableField<TData> = IColumn<TData> & {
        Order: CTableStatusOrder;
        /** Indicador de la ubcación real del valor
         * @example
         * * this.Field == "Nombre" ? false
         * * this.Field == "ValorObjeto.Nombre" ? true
         * @deprecated
         */
        IsLowLevel: boolean; // FIXME // REMOVER
        MaxWidth?: string;
        OriginalMinWidth: string;
    }

    interface IRowController<TData, TDataChild> {
        /** En caso de que el dato no se encuentre su valor será undefined */
        readonly Id: TTableIdType;
        readonly IsChecked: boolean;
        readonly IsIndeterminateChecked: boolean;
        readonly HasActiveSelected: boolean;
        readonly IsEnable: boolean;
        readonly IsCollapsed: boolean;
        //readonly IsCollapse: boolean;
        /** En caso de que el dato no se encuentre su valor será undefined */
        Data: TData;
        GetChildrenData: <TD = TDataChild, TDChild = unknown>() => Map<TTableIdType, IRowController<TD, TDChild>>;
        GetChildrenDataFiltered: <TD = TDataChild, TDChild = unknown>() => Map<TTableIdType, IRowController<TD, TDChild>>;
        /** Selecciona un elemento dentro de los datos colapsables del item seleccionado actual */
        SelectChildItem: <TDataChild, TDataChildThreeLv>(id: TTableIdType) => IRowController<TDataChild, TDataChildThreeLv>;
        /** Cambia el estado "Checkeado" del dato, no actualiza la tabla, hasta que se refresque la tabla */
        CheckItem: (check: boolean) => IRowController<TData, TDataChild>;
        /** Cambia el estado "Habilitado" del dato, no actualiza la tabla, hasta que se refresque la tabla */
        EnableItemRow: (enable: boolean) => IRowController<TData, TDataChild>;
        /** Cambia el estado "Colapsado" de la fila, no actualiza la tabla, hasta que se refresque la tabla */
        CollapseItem: (collapse: boolean) => IRowController<TData, TDataChild>;
        // RemoveFromTable: () => ISelectedItem<TData>;
        /** Refresca los elementos UI de la tabla */
        RefreshTableView: () => IRowController<TData, TDataChild>;
        // Collapse: (collapse: boolean) => void;
    }

    type TDataAsInnerText<TData> = { [k in keyof TData & string]: string }

    /** Nota: Las filas tienden a actualizarse en cada Intersection.
     * * La propiedad almacena un valor booleano en los nodos de las filas (.tr_body)
     * con el que IntersectionObserver puede verificar si d3.join ha actualizado el datum de la fila.
     * * En caso de que el valor sea falso (la fila tiene nuevo datum), IntersectionObserver lanza la aztualización del UI de la fila.
     *
     * @valuetype boolean
    */
    const prop_row_updatedview = "_UpdatedView";
    /** @valuetype TStepJoin */
    const prop_row_stepjoin = "_StepJoin";


    export class Tabla<TDataTable> {
        #floatingOptShowTimeOut: NodeJS.Timeout;
        #floatingHideShowTimeOut: NodeJS.Timeout;
        private props: IPropieties<TDataTable>;
        private elements: IControlesGenerales<TDataTable>;
        private config: ITableConfig<TDataTable>;
        private levelsConfig: Map<number, ILevelEvaluator>;

        private hasCollapseColumn: boolean;
        private intersectedObserverRowsPrincipal: IntersectionObserver;
        private timeOutToPrincipalRows: NodeJS.Timeout;
        private orderItemsOutTimeOut: boolean;
        private tableIdentifier: string;
        private ctrlFloatingOptions: FloatingOptionsBtns;

        private tableReorderer: TableReorderer<TDataTable>;
        private tableResizer: TableResizer<TDataTable>;
        private selectionManager: TableSelectionManager;

        private dataBuilder: TableDataBuilder;

        constructor(config: ITableConfig<TDataTable>) {

            this.props = {
                DataOrigin: [],
                DataOriginTableMap: new Map(),
                DataOriginTableArray: [],
                DataTableFilteredMap: new Map(),
                DataTableFilteredArray: [],
                ReliableCurrentData: true,
                ColumnHeaders: [],
            };
            this.levelsConfig = new Map();

            this.tableReorderer = new TableReorderer(this);
            this.tableResizer = new TableResizer(this);
            this.selectionManager = new TableSelectionManager();
            this.dataBuilder = new TableDataBuilder();

            this.hasCollapseColumn = false;
            idCount++;
            this.tableIdentifier = PREFIX_IDENTIFIER + idCount;
            this.InitElements();
            this.SetConfig(config);
            this.tableResizer._BuildSizeStylesConfig();
            this.BuildTablefFloatingOptions();
            this.ControllerIntersectedObserver();
            this.tableResizer._TableResizeStartObserver();

            this.selectionManager.updateContext(
                this.props.DataTableFilteredArray,
                this.props.DataOriginTableArray
            );
            this.dataBuilder.updateContext(
                this.levelsConfig,
                this.elements.C_CtrlFiltrosV2,
            );
        }

        private InitElements() {
            this.elements = <IControlesGenerales<TDataTable>>{}

            this.elements.DivContent = d3.create<HTMLDivElement>("html:div")
                .classed("tabla_container flex_colcontrol " + this.tableIdentifier, true)
            this.elements.A_DivAcciones = this.elements.DivContent.append<HTMLDivElement>("div")
                .classed("area_acciones", true)
                .classed("hide", true)

            this.elements.A2_DivFiltros = this.elements.DivContent.append<HTMLDivElement>("div")
                .classed("area_filtros", true)
                .classed("hide", true)

            this.elements.B_DivControl = this.elements.DivContent.append<HTMLDivElement>("div")
                .classed("area_control flex_colcontrol", true)

            this.elements.AA_DivMenuOfChecks = this.elements.A_DivAcciones.append<HTMLDivElement>("div")
                .style("height", "100%")
                .style("max-width", "0px")

            this.elements.BA_DivSpaceTabla = this.elements.B_DivControl.append<HTMLDivElement>("div")
                .classed("space_tabla", true)

            this.elements.BA_SpaceTablaStyleSizing = this.elements.BA_DivSpaceTabla.append("style").classed("sizing_table_pref", true)
            this.elements.BA_SpaceTablaSwitchLastCellSizing = this.elements.BA_DivSpaceTabla.append("style").classed("table_switch_last_col", true)

            this.elements.BA_DivSpaceTabla.append("div")
                .classed("dim_width", true);
            this.elements.BAAA_TablaHead = this.elements.BA_DivSpaceTabla.append<HTMLDivElement>("div")
                .classed("rhead", true)
            this.elements.BAAB_TablaBodyA = this.elements.BA_DivSpaceTabla.append<HTMLDivElement>("div")
                .classed("rbodyA", true)
            this.elements.BAAB_TablaBodyB = this.elements.BA_DivSpaceTabla.append<HTMLDivElement>("div")
                .classed("rbodyB", true)

            this.elements.BB_DivSpacePie = this.elements.B_DivControl.append<HTMLDivElement>("div")
                .classed("space_pie hide", true);
        }

        private SetConfig(config: ITableConfig<TDataTable>) {
            this.config = Object.assign({}, config);

            // CONFIGS POR DEFAULT //
            this.config.EnableRelevanceSelections = (config.EnableRelevanceSelections != null ? config.EnableRelevanceSelections : true);
            this.config.EnableStickyFields = (config.EnableStickyFields != null ? config.EnableStickyFields : false);
            this.config.StickyCheckInRow = (config.StickyCheckInRow != null ? config.StickyCheckInRow : false);
            this.config.HideCheckboxes = (config.HideCheckboxes != null ? config.HideCheckboxes : false);
            this.config.AddNameFieldClassToCells = (config.AddNameFieldClassToCells != null ? config.AddNameFieldClassToCells : false);
            this.config.HideItemWhenItHasNoChildItems = (config.HideItemWhenItHasNoChildItems != null ? config.HideItemWhenItHasNoChildItems : true);
            this.config.DisabledRowsWithMenuInRow = (config.DisabledRowsWithMenuInRow != null ? config.DisabledRowsWithMenuInRow : false);
            this.config.FilterByStrSearch = (config.FilterByStrSearch == null ? "default" : config.FilterByStrSearch);
            this.config.MenuInRowNoCellsToIgnoreWidth = (this.config.MenuInRowNoCellsToIgnoreWidth != null ? this.config.MenuInRowNoCellsToIgnoreWidth : 1);
            this.config.Resizable = (config.Resizable != null ? config.Resizable : true);
            this.config.Reorderable = (config.Reorderable != null ? config.Reorderable : true);

            // CONFIGURACIONES EN NIVELES //
            const AddConfigsLevels = (level: number, evalConfig: ILevelEvaluator) => {
                evalConfig.Level = level;
                evalConfig.IdMember = (evalConfig.IdMember ? evalConfig.IdMember : this.config.IdData);
                evalConfig.ActiveDataOnlyWhenDisplayData = evalConfig.ActiveDataOnlyWhenDisplayData || false;
                this.levelsConfig.set(level, evalConfig);

                if (evalConfig.EvaluatorSubLevel) {
                    AddConfigsLevels(level + 1, <ILevelEvaluator>evalConfig.EvaluatorSubLevel);
                }
            }

            AddConfigsLevels(1, <ILevelEvaluator>{
                IdMember: this.config.IdData as string,
                EvaluatorSubLevel: this.config.EvaluatorAndSubLevelsBuild?.EvaluatorSubLevel,
                AddRowMultiline: this.config.EvaluatorAndSubLevelsBuild?.AddRowMultiline,
                ActiveDataOnlyWhenDisplayData: this.config.EvaluatorAndSubLevelsBuild?.ActiveDataOnlyWhenDisplayData,
                ForceHideLevelRowsWithoutChilds: this.config.EvaluatorAndSubLevelsBuild?.ForceHideLevelRowsWithoutChilds,
                ShowNoContentTagRow: this.config.EvaluatorAndSubLevelsBuild?.ShowNoContentTagRow,
                HideLevelCollapseIndicator: this.config.EvaluatorAndSubLevelsBuild?.HideLevelCollapseIndicator,
                GetOptionsInRowV2: this.config.EvaluatorAndSubLevelsBuild?.GetOptionsInRowV2,
                OnEvalIsCollapsedRow: this.config.EvaluatorAndSubLevelsBuild?.OnEvalIsCollapsedRow,
                OnEvalIsEnableRow: this.config.EvaluatorAndSubLevelsBuild?.OnEvalIsEnableRow,

                OnCollapserChange: this.config.EvaluatorAndSubLevelsBuild?.OnCollapserChange,
                OnActiveSelectedData: this.config.EvaluatorAndSubLevelsBuild?.OnActiveSelectedData,

                OnStepRowTable: this.config.EvaluatorAndSubLevelsBuild?.OnStepRowTable,
                OnStepCellTable: this.config.EvaluatorAndSubLevelsBuild?.OnStepCellTable,
            });

            // SUB CONTROLES DE TABLA EN BASE A LA CONFIGURACIÓN //

            this.elements.BA_DivSpaceTabla.classed("without_checkboxes", this.config.HideCheckboxes);
            this.elements.BA_DivSpaceTabla.classed("without_collapsers", !this.hasCollapseColumn);
            this.elements.BA_DivSpaceTabla.classed("auxpadding", !this.hasCollapseColumn && this.config.HideCheckboxes);

            if (this.config.OptionsTopDefaultV2 || this.config.Title) {
                this.elements.A_DivAcciones.classed("hide", false);

                this.MenuTopDefaultOptions_UpdateOptions();
                this.MenuTopDefaultOptions_UpdateTitle();
            }

            if (this.config.OptionsOfActiveSelectedDataV3) {
                this.elements.A_DivAcciones.classed("hide", false);
            }

            this.BuildColumnsConfig();

            this.BuildFilters();

            if (this.config.OnClickSyncTable) {
                let btnSync = this.elements.A_DivAcciones.append("div")
                    .classed("area_sync", true)
                    .append("img")
                    .attr("src", UIUtilIconResources.CGeneral.Sync)
                    .attr("draggable", false);

                btnSync.on("click", async () => {
                    btnSync.classed("anim_syncing", true);
                    let dataToUpdate = await this.config.OnClickSyncTable();
                    this.UpdateData(dataToUpdate);
                    btnSync.classed("anim_syncing", false);
                })
            }

            if (this.config.MinWidth || this.config.Resizable) {
                this.elements.BA_DivSpaceTabla.append("style");
                this.tableResizer._ApplyGeneralRowsMinWidth(this.config.MinWidth);
            }

            this.elements.BA_DivSpaceTabla.node().onscroll = () => this.tableResizer._TableApplyScrollX();
            this.config.Parent.append<HTMLDivElement>(() => this.elements.DivContent.node());
            this.UpdateColumnHeaders();
        }

        private BuildColumnsConfig() {
            this.props.ColumnHeaders = this._ConfigColumnsHeaders;
            this.config.Resizable && this.tableResizer._LoadColumnsDimensionsCfgFromLS();
            this.config.Reorderable && this.tableReorderer._LoadColumnsOrderCfgFromLS()
        }

        private GetTableObjectConfig_LS(key: "tabledimconfig" | "tableorderconfig" = "tabledimconfig") {
            let strTableConfig = DataUtilLocalStorage._GetItem("winstatus", key)
            return (strTableConfig ? JSON.parse(strTableConfig) : {})
        }

        private BuildFilters() {
            const hasFilters = this.config.FilterParameters?.length > 0 || this.config.FilterByStrSearch != "none";
            this.elements.A2_DivFiltros.classed("area_filtros flex_rowcontrol", hasFilters).classed("hide", !hasFilters);
            if (hasFilters) {
                let onGetValuesToFilterBySearch: (dato: TableNode<TDataTable>) => string[];
                const paramsFilter = this.config.FilterParameters || [];

                if (this.config.FilterByStrSearch == "default") {
                    onGetValuesToFilterBySearch = (dato) => {
                        if (dato.levelRow == 1) {
                            const setFullFields = new Set([
                                ...this.props.ColumnHeaders.map(d => d.Field),
                                ...paramsFilter.map(d => d.Field)
                            ]);
                            return Array.from(setFullFields)
                                .filter(field => (typeof dato.data[field] == "string"))
                                .map(field => (dato.data[field] as unknown as string));
                        }
                        return null;
                    }
                }
                else if (this.config.FilterByStrSearch != "none") {
                    onGetValuesToFilterBySearch = (dato) => {
                        if (dato.levelRow == 1) {
                            return (this.config.FilterByStrSearch as Function)(dato.data);
                        }
                        return null;
                    }
                }
                const mainFilters: IMainFilterItem[] = paramsFilter
                    .map((d) => {
                        let label = d.Label;
                        if (d.LabelLangKey && d.LangModuleKeyInContext) {
                            label = UIUtilLang._GetUIString(d.LangModuleKeyInContext, d.LabelLangKey);
                        } else if (d.LabelLangKey) {
                            label = this.GetLangContext(d.LabelLangKey);
                        }
                        d.Type = d.Type || "text";
                        d.Level = d.Level || 1;

                        return <IMainFilterItemTypeConfig<TMainFilterType>>{
                            Key: d.Field,
                            Type: d.Type,
                            Label: label,
                            Min: (d as ITableParametroFiltroMap<any, "number">).Min,
                            Max: (d as ITableParametroFiltroMap<any, "number">).Max,
                            MinDate: (d as ITableParametroFiltroMap<any, "date">).MinDate,
                            MaxDate: (d as ITableParametroFiltroMap<any, "date">).MaxDate,
                            Options: (d as ITableParametroFiltroMap<any, "select">).Options,
                            OnGetAdvancedFilterDraw: (d as ITableParametroFiltroMap<any, "advanced">).OnGetAdvancedFilterDraw,
                            GetAdvancedFilterValue: (d as ITableParametroFiltroMap<any, "advanced">).GetAdvancedFilterValue,
                            OnStepAdvancedFilter: (d as ITableParametroFiltroMap<any, "advanced">).OnStepAdvancedFilter,
                            OnChange: d.OnChange,
                            OnGetValueData: d.Type == "advanced" ? null : (dato: TableNode<TDataTable>) => {
                                if (dato.hasActiveSelection || dato.levelRow != d.Level) {
                                    // console.error("El filtro ", d.Field, " no aplica al nivel ", d.Level); // REMOVER
                                    return null;
                                }
                                let values: any[];
                                if (d.OnGetValueToMatch) {
                                    values = d.OnGetValueToMatch(dato.data);
                                } else {
                                    values = [UIUtilGeneral._GetValueLevelsObj(d.Field, dato.data)];
                                }

                                switch (d.Type) {
                                    case "text":
                                        values = values.map(v => (v ? v + "" : null)); // Se ajustan los valores a string
                                        break;
                                    case "number":
                                        break;
                                }
                                return values;
                            },
                        } as IMainFilterItem;
                    });

                if (!this.elements.C_CtrlFiltrosV2) {
                    this.elements.C_CtrlFiltrosV2 = new FilterControlV2({
                        ID: `table-filter-${this.config.IdTabla}`,
                        Parent: this.elements.A2_DivFiltros,
                        MainFilters: mainFilters,
                        OnChangeFilters: () => {
                            // console.warn("-d", "Filter change");
                            this.RefreshFilterMainData();
                            this.props.DataTableFilteredArray.forEach(d => {
                                d.syncCheckStateWithChildren(); // NOTE: solo para el nivel 1
                            });
                            this.UpdateDataView(true, true);
                        },
                        OnGetValuesToFilterBySearch: onGetValuesToFilterBySearch
                    });
                }
                this.elements.C_CtrlFiltrosV2._SetMainFilters(mainFilters);
            }
        }

        private ControllerIntersectedObserver() {
            this.intersectedObserverRowsPrincipal = new IntersectionObserver(
                (entries) => {
                    entries
                        .forEach((entry) => {
                            // let level = entry.target["_level"];
                            let trBody = d3.select<HTMLDivElement, TableNode<TDataTable>>(entry.target as HTMLDivElement)
                            // tr.classed("hide", !entry.isIntersecting);

                            if (entry.isIntersecting) {
                                trBody//.style("pointer-events", null)
                                    .style("min-height", null);

                                const joinStep = entry.target[prop_row_stepjoin] as TStepJoin;

                                if (entry.target[prop_row_updatedview]) {
                                    return;
                                }
                                entry.target[prop_row_updatedview] = true;


                                let datum = trBody.datum();

                                this.UpdateRowData(trBody, datum, joinStep);
                            }
                            // else {
                            //     trBody.style("pointer-events", "none");
                            // }
                        })
                },
                {
                    root: this.elements.BA_DivSpaceTabla.node(),
                    // threshold: 0
                    // rootMargin: "50px"
                }
            );
        }

        // NOTE INICIO DE DATOS
        private UpdateData(data: Array<TDataTable> = []) {
            let lastReliableData = this.props.ReliableCurrentData;

            const result = this.dataBuilder.buildData(
                data,
                lastReliableData,
                this.props.DataOriginTableMap,
            )

            this.props.DataOrigin = data;
            this.props.DataOriginTableMap = result.map;
            this.props.DataOriginTableArray = result.array;

            // Actualizar banderas locales
            this.props.ReliableCurrentData = this.dataBuilder.isDataReliable;
            this.hasCollapseColumn = this.dataBuilder.hasCollapseColumn;

            // sincronizar MANAGERS
            this.selectionManager.updateContext(
                this.props.DataTableFilteredArray,
                this.props.DataOriginTableArray
            )

            this.RefreshFilterMainData();

            if (this.levelsConfig.get(1).OnActiveSelectedData && this.selectionManager.hasActiveSelectedNodes()) {
                const activeSelectedData = this.selectionManager.getActiveSelectedData();
                this.levelsConfig.get(1).OnActiveSelectedData(
                    activeSelectedData,
                    CTableOriginEventType.OnUpdateDataTable,
                    null,
                    null
                );
            }

            this.UpdateDataView(true, true);
        }

        private RefreshFilterMainData() {
            this.props.DataTableFilteredArray = this.GetFiltrarDatos(this.props.DataOriginTableArray, this.props.DataOriginTableArray, true);
            this.props.DataTableFilteredMap = new Map(this.props.DataTableFilteredArray.map(d => ([d.id, d])));
            this.selectionManager.updateContext(
                this.props.DataTableFilteredArray,
                this.props.DataOriginTableArray
            );
            // this.UpdateDataView(true, true);
        }

        /** Evalua antes si el filtro es posible. Retorna un arreglo nuevo */
        private GetFiltrarDatos(
            dataFull: Map<TTableIdType, TableNode<any>> | Array<TableNode<any>>,
            lastFilteredData: TableNode<any>[],
            /**  */
            enableRowLevel: boolean
        ): TableNode<any>[] {

            let data = Array.isArray(dataFull) ? dataFull : Array.from(dataFull.values());
            let finalData: TableNode<any>[] = [];
            const level = data[0]?.levelRow;

            if (level == 1 && this.config.FilterExtraBeforeFilterTable) {
                data = data.filter(d => this.config.FilterExtraBeforeFilterTable(d.data));
            }

            if (!this.elements.C_CtrlFiltrosV2) {
                finalData = [...data];
            }
            else if (!enableRowLevel) {
                finalData = [...lastFilteredData];
            }
            else if (data.length) {
                finalData = this.elements.C_CtrlFiltrosV2._FilterData(
                    data,
                    (level == 1),
                    this.GetKeysFiltersByLevel(level).map(d => d.Field)
                )
                finalData.forEach(d => {
                    if (d.childDataOriginMap.size || d.childDataFilteredArray.length) {
                        // const [item] = d.childDataOriginMap.values();
                        // const tieneFiltros = item ? this.config.FilterParameters?.some(d => d.Level == item.levelRow) : false;
                        // !tieneFiltros || !d.hasActiveSelection;
                        const habilitarFiltrarSubnivel = true; // NOTE: los datos checkeados se ignoran en `OnGetValueData`
                        d.childDataFilteredArray = this.GetFiltrarDatos(d.childDataOriginMap, d.childDataFilteredArray, habilitarFiltrarSubnivel);
                    }
                });
                const levelConfig = this.levelsConfig.get(level);
                if (this.levelsConfig.has(level + 1) && levelConfig.ActiveDataOnlyWhenDisplayData == false && levelConfig.ForceHideLevelRowsWithoutChilds) {
                    finalData = finalData.filter(d => (d.childDataOriginMap.size && d.childDataFilteredArray.length));
                }
            }
            if (dataFull == this.props.DataOriginTableArray && this.config.OnFilter) setTimeout(() => {
                this.config.OnFilter();
            });
            return finalData;
        }

        private GetKeysFiltersByLevel(level: number): TTableParametroFiltro<TDataTable>[] {
            if (this.config.FilterParameters) {
                return this.config.FilterParameters
                    .filter(d => (d.Level == level));
            }
            return [];
        }

        /** Dibuja filas de la página actual
        * @param isAB boolean: hace referencia al orden en que se van a actualizar las areas rBodyA y rBodyB
        * @param orderData Si es true; vuelve a agrupar paginas //  TEMPORAL Comentario
        * @param doOnEndUpdateDataInView ayuda a evitar bucles en caso de invocar un refres de table desde this.config.OnEndUpdateDataInView
        */
        private UpdateDataView(isAB: boolean = true, orderData: boolean = false, doOnEndUpdateDataInView: boolean = true): void {
            // console.debug("TABLE -> UpdateDataView invocado...  ", date.toISOString());
            if (this.timeOutToPrincipalRows) {
                clearTimeout(this.timeOutToPrincipalRows);
            }

            if (!this.orderItemsOutTimeOut && orderData) {
                this.orderItemsOutTimeOut = true;
            }

            this.timeOutToPrincipalRows = setTimeout(() => {
                this.timeOutToPrincipalRows = null;

                // console.debug("TABLE -> UpdateDataView ejecutando...", this.propiedades.DataTable, this.propiedades.DataTable.size, this.propiedades.PagActual);
                if (this.orderItemsOutTimeOut) {
                    this.props.DataTableFilteredArray = this.Order_GetRefreshCurrentOrder(this.props.DataTableFilteredArray, 1);
                    this.selectionManager.updateContext(
                        this.props.DataTableFilteredArray,
                        this.props.DataOriginTableArray
                    );
                    this.orderItemsOutTimeOut = false;
                }

                // let datos: Array<TableNode<TDataTable>> = this.GetFilteredItems(); // auxPaginaData ? auxPaginaData.Data : []
                let datos: TableNode<TDataTable, any>[];
                let dataPinned: TableNode<TDataTable, any>[];

                if (this.config.EnableRelevanceSelections) {
                    datos = this.selectionManager.getUnActiveSelectedFilteredNodes();
                    dataPinned = this.selectionManager.getActiveSelectedNodes().reverse();
                } else {
                    datos = this.props.DataTableFilteredArray;
                    dataPinned = [];
                }

                // console.debug(`TABLE -> UpdateDataView >> TotalData: ${this.propiedades.DataTable.size}, Cheks: ${dataChecked.length}, PageData: ${data.length} Page: ${this.propiedades.PagActual}`);
                let tbodyA = this.elements.BAAB_TablaBodyA
                let tbodyB = this.elements.BAAB_TablaBodyB

                this.elements.BA_DivSpaceTabla.selectAll("tr").classed("tr_selected", false);
                this.elements.BA_DivSpaceTabla.selectAll(".tr_body").interrupt();

                this.UpdateColumnHeaders();

                if (datos.length == 0) {
                    tbodyA
                        .style("max-height", "100%")
                        .style("flex", "auto");
                    tbodyB.classed("hide", true);
                } else {
                    tbodyA
                        .style("max-height", "40%")
                        .style("flex", "none");
                    tbodyB.classed("hide", false);
                }
                if (this.config.EnableRelevanceSelections) {
                    tbodyA.classed("border_bottom_2", dataPinned.length > 0 && datos.length > 0);
                }

                const fnUseAnimation = (cont: TSelectionHTML, data: any[]) => (this["__lastcountdata"] > 100 || this["__lastcountdatachecked"] > 100 || datos.length > 100 || dataPinned.length > 100)
                    ? false
                    : (cont.node().hasChildNodes() || data.length > 0)

                if (isAB) {
                    // console.error("RenderData 1: ", dataChecked);
                    if (this.config.EnableRelevanceSelections) {
                        this.DrawData(dataPinned, tbodyA, 1, fnUseAnimation(tbodyA, datos));
                    }
                    // console.error("RenderData 2: ", data)
                    this.DrawData(datos, tbodyB, 1, fnUseAnimation(tbodyB, dataPinned));
                } else {
                    this.DrawData(datos, tbodyB, 1, fnUseAnimation(tbodyB, dataPinned));
                    if (this.config.EnableRelevanceSelections) {
                        this.DrawData(dataPinned, tbodyA, 1, fnUseAnimation(tbodyA, datos));
                    }
                }

                this.UpdateStickyFields();

                if (this.config.OnEndUpdateDataInView && doOnEndUpdateDataInView) {
                    // this.config.OnEndUpdateDataInView(this.propiedades.PagActualOLD, datos, dataChecked);
                    this.config.OnEndUpdateDataInView(datos, dataPinned);
                }

                this["__lastcountdata"] = datos.length
                this["__lastcountdatachecked"] = dataPinned.length
            }, 150);

            this.elements.BA_DivSpaceTabla.classed("without_collapsers", !this.hasCollapseColumn);
        }

        private BuildRowAndCells(trBody: TSelectionHTML<"div", TableNode<TDataTable>>, level: number) {
            const columnsConfig = this.props.ColumnHeaders;
            const levelConfig = this.levelsConfig.get(level);

            let tr = trBody.append<HTMLTableRowElement>("tr")
                .classed("principal", true)

            // Check por fila
            let tdToCheck = tr.append("td")
                .classed("actioncell checkcell", true)
                .classed("sticky", this.config.StickyCheckInRow);

            if (!levelConfig.RemoveLevelCheckboxes) {
                tdToCheck.append(() => this.Check_GetCheckBoxDrawing(levelConfig.Level));
            }

            tr.append("td")
                .classed("actioncell collapsercell", true)
            // .classed("sticky", this.config.StickyCheckInRow);

            columnsConfig.forEach(item => {
                tr.append("td")
                    .text("◽◽◽")
                    .classed("field", true)
                    .classed(item.Field, true)
                    .style("justify-content", item.Align);
            })

            this.tableResizer._AppendAuxCellResize(tr);

            tr.append<HTMLTableCellElement>("td")
                .classed("sticky lastcell", true)

            if (level > 1) {
                tr.selectAll("td").style("background", this.GetRGBColorToLevelRow(level));
            }

            return tr;
        }

        private UpdateRowData(rowBody: TSelectionHTML<"div", TableNode<TDataTable>>, dataItemT: TableNode<TDataTable>, typeUpdate: TStepJoin) {
            const columnsConfig = this.props.ColumnHeaders;
            const levelConfig = this.levelsConfig.get(dataItemT.levelRow);
            const nextLevel = dataItemT.levelRow + 1;

            let trRowToDatum: TSelectionHTML<"tr">;

            // >> BUILD ROW & CELLS
            if (rowBody.classed("waiting-build")) {
                trRowToDatum = this.BuildRowAndCells(rowBody, dataItemT.levelRow);
                rowBody.classed("waiting-build", false);
            } else {
                trRowToDatum = rowBody.select<HTMLTableRowElement>(":scope > .principal");
            }

            // >> UPDATE ROW & CELLS
            const tds = (() => {
                if (trRowToDatum.node().firstElementChild instanceof HTMLTableCellElement) {
                    return trRowToDatum.selectAll<HTMLTableCellElement, any>(":scope>.field").nodes(); //trRow.node().cells
                } else {
                    return trRowToDatum.selectAll<HTMLTableCellElement, any>(":scope>.trorigin>.field").nodes(); //trRow.node().cells
                }
            })()
            const ctrlCheckInRow: CheckboxComponent = trRowToDatum.select<CheckboxComponent>(".checkbox_table").node(); // this.elements.CC_CtrlsChecks.get(dataItemT.id)

            trRowToDatum.node().onclick = null;
            trRowToDatum.node().ondblclick = null;

            const UpdateDatumRowBody = () => {
                rowBody.datum(dataItemT);
            }

            if (ctrlCheckInRow) {
                ctrlCheckInRow.onclick = null;
                ctrlCheckInRow.onclick = (e) => {
                    this.Check_CheckItemsData([dataItemT], !dataItemT.isChecked, CTableOriginEventType.OnClickRowTopCheck);
                    UpdateDatumRowBody();
                    e.stopPropagation();
                }
                this.Check_UpdateCheckBoxStatus(dataItemT, ctrlCheckInRow);
            }
            else if (!levelConfig.RemoveLevelCheckboxes) {
                console.warn("checkInRow NoFound: ", dataItemT);
            }

            if (typeUpdate == "exit") {
                return;
            }

            // const defaultCells = ["checkcell", "collapsercell", "lastcell"];
            const lastClass = "lastcell";
            const nColRequired = (columnsConfig.length)// + defaultCells.length);
            const colsChanged = tds.length != nColRequired;
            if (colsChanged) {
                // console.warn(trRowToDatum.node(), tds)
                // las columnas cambiaron
                if (tds.length > nColRequired) {
                    // const dataCols = tds.filter((td) => !defaultCells.some(def => (td.classList.contains(def))))
                    const dif = tds.length - nColRequired;
                    for (let i = 0; i < dif; i++) {
                        tds[i].remove();
                    }
                } else {
                    const dif = nColRequired - tds.length;
                    const lastCell = trRowToDatum.select<HTMLTableCellElement>("." + lastClass).node()
                    // .text("◽◽◽")
                    for (let i = 0; i < dif; i++) {
                        const newCell = d3.create("td").classed("field", true).node();
                        lastCell.insertAdjacentElement("beforebegin", newCell);
                    }
                }
            }

            for (let i = 0; i < columnsConfig.length; i++) {
                // UPDATE CELL
                const configCol = columnsConfig[i];
                const td = d3.select(tds[i]);
                let cellContent = td.select<HTMLDivElement>(".item_cont");

                if (!tds[i]) {
                    // console.warn("field cell no found -> ", dataItemT.id, configCol.Field, dataItemT.data);
                    continue;
                }

                if (td.classed(".lastcell")) {
                    continue
                }

                td.node().onclick = null;

                if (colsChanged) {
                    cellContent.text(""); // RESET COL
                }

                cellContent = this.UpdateCellView(levelConfig, configCol, dataItemT, td, cellContent);

                UIUtilElementBehaviors._EllipsisTextTooltip(cellContent.node());

                if (configCol.OnClickInCellCont) {
                    cellContent.node().onclick = (e) => {
                        configCol.OnClickInCellCont(dataItemT.data, e);
                        e.stopPropagation();
                    }
                }
            }

            // >> MENU IN ROW

            if (dataItemT.isRowEnable || this.config.DisabledRowsWithMenuInRow) {
                let rowClickTimeout: ReturnType<typeof setTimeout> = null;
                trRowToDatum.node().onclick = (e) => {
                    if (e.detail > 1) return;

                    if (rowClickTimeout) {
                        clearTimeout(rowClickTimeout);
                    }

                    rowClickTimeout = setTimeout(() => {
                        if (!window.getSelection().toString()) {
                            if (this.config.OnValueSelectRow && dataItemT.levelRow == 1) { // NOTE aun limitado al nivel 1
                                const isSelected = trRowToDatum.classed("tr_selected");
                                this.elements.BA_DivSpaceTabla.selectAll("tr").classed("tr_selected", false);
                                trRowToDatum.classed("tr_selected", !isSelected); // Toggle style
                                this.config.OnValueSelectRow(dataItemT.id, dataItemT.data, trRowToDatum.classed("tr_selected"));
                            }
                            if (levelConfig.GetOptionsInRowV2) {
                                this.MenuRowSelectionOptions_Toggle(dataItemT, trRowToDatum);
                            }
                        }
                        rowClickTimeout = null;
                    }, 220);
                }

                trRowToDatum.node().ondblclick = () => {
                    if (rowClickTimeout) {
                        clearTimeout(rowClickTimeout);
                        rowClickTimeout = null;
                    }
                }
                if (this.MenuRowSelectionOptions_EvalOldAndNewData(dataItemT)) {
                    this.MenuRowSelectionOptions_UpdateData(dataItemT, trRowToDatum);
                }
            } else {
                if (ctrlCheckInRow) {
                    ctrlCheckInRow.onclick = null;
                }
                trRowToDatum.node().onclick = null;
                trRowToDatum.node().ondblclick = null;
                if (this.MenuRowSelectionOptions_EvalOldAndNewData(dataItemT)) {
                    this.MenuRowSelectionOptions_Remove();
                }
            }

            trRowToDatum.classed("tr_disabled", !dataItemT.isRowEnable);
            if (ctrlCheckInRow) ctrlCheckInRow.em_disabled = !dataItemT.isRowEnable;

            // >> COLLAPSER THINGS

            let btnCollapse: TSelectionHTML<"wc-ic-collapse">;
            let errorGetData: Error = null;
            const UpdateViewChilds = async (isFromClick: boolean) => {
                errorGetData = null;
                if (btnCollapse?.node()) {
                    btnCollapse.node()._Collapsed = dataItemT.isCollapsed;
                }
                if (levelConfig.HideLevelCollapseIndicator) {
                    btnCollapse?.classed("hide", true);
                }

                if (!dataItemT.isCollapsed) {
                    if ((!dataItemT.hasActiveSelection || isFromClick) && levelConfig.ActiveDataOnlyWhenDisplayData && levelConfig.EvaluatorSubLevel?.OnGetData) {
                        let getData = levelConfig.EvaluatorSubLevel?.OnGetData(dataItemT.data, TableUtilsGeneral.getParentsDataArray(dataItemT));
                        let subData = [];
                        if (getData instanceof Promise) {
                            Button._EnableButton(btnCollapse, false);
                            let spinner = trRowToDatum.select(".collapsercell")
                                .append<HTMLSpinnerElement>("wc-spinner")
                                .attr("dim", 20)
                                .attr("border-width", 3)
                                .attr("center", true);
                            let dataResponse = await getData;
                            if (dataResponse instanceof Error) {
                                errorGetData = dataResponse;
                            } else {
                                subData = dataResponse;
                            }
                            spinner.remove();
                            Button._EnableButton(btnCollapse, true);
                        } else {
                            subData = getData;
                        }

                        this.dataBuilder.updateBranch( // Actualiza dataItemT.childDataOriginMap
                            dataItemT,
                            subData ? subData : []
                        );
                        const chilTieneFiltros = this.config.FilterParameters?.some(d => d.Level == dataItemT.levelRow + 1);
                        dataItemT.childDataFilteredArray = this.GetFiltrarDatos(
                            dataItemT.childDataOriginMap,
                            dataItemT.childDataFilteredArray,
                            chilTieneFiltros ? !dataItemT.isChecked : true
                        );
                    }
                }

                const hasDataToShow = (dataItemT.childDataFilteredArray.length > 0);
                if (hasDataToShow) { // dataItemT.dataCollapser.size > 0) {
                    if (!dataItemT.isCollapsed) {
                        dataItemT.childDataFilteredArray = this.Order_GetRefreshCurrentOrder(dataItemT.childDataFilteredArray, nextLevel);
                        this.DrawData(dataItemT.childDataFilteredArray, rowBody, nextLevel);
                    } else {
                        if (dataItemT.childDataFilteredArray.length < 20) {
                            this.DrawData([], rowBody, nextLevel); // DOTEST Pruebas de rendimiento con ambas formas
                        } else {
                            rowBody.selectAll(".tr_body").remove();
                        }
                    }
                } else {
                    // this.DrawData([], rowBody, nextLevel); // DOTEST Pruebas de rendimiento con ambas formas
                    rowBody.selectAll(".tr_body").remove();
                }

                if ((levelConfig.ShowNoContentTagRow || levelConfig.ActiveDataOnlyWhenDisplayData) && !dataItemT.isCollapsed && (!hasDataToShow || errorGetData)) {
                    // Fila desagrupada y sin datos
                    let rowNoDataInfo = rowBody.select<HTMLDivElement>(".info_nodata");
                    if (!rowNoDataInfo.node()) {
                        rowNoDataInfo = rowBody.append("div")
                            .attr("class", "tr_body row_extra info_nodata")

                        rowNoDataInfo.append("tr")
                            .attr("class", "principal")
                            .append("td")
                            .attr("class", "sticky lastcell")
                            .style("min-width", "100%")
                            .style("justify-content", "center", "important")
                            .style("height", "40px")
                            .style("background", this.GetRGBColorToLevelRow(nextLevel))
                            .style("font-weight", "bold")
                            .style("color", errorGetData ? "var(--color_app_red1)" : null)
                            .text(errorGetData ? errorGetData.message : UIUtilLang._GetUIString("general", "sincontenido"));
                    }
                }
            }

            if (levelConfig.ShowNoContentTagRow || levelConfig.ActiveDataOnlyWhenDisplayData || dataItemT.childDataFilteredArray.length > 0) {
                btnCollapse = trRowToDatum.select(".collapsercell > wc-ic-collapse");
                if (!btnCollapse.node()) {
                    btnCollapse = trRowToDatum.select(".collapsercell").append<HTMLIconCollapseElement>("wc-ic-collapse");
                }

                btnCollapse.node().onclick = async (e) => {
                    dataItemT.isCollapsed = !dataItemT.isCollapsed;
                    UpdateDatumRowBody();
                    UpdateViewChilds(true);
                    if (levelConfig.OnCollapserChange) {
                        levelConfig.OnCollapserChange(dataItemT.isCollapsed, dataItemT.data, trRowToDatum);
                    }
                    e.stopPropagation();
                }
            } else {
                trRowToDatum.select(".collapsercell > wc-ic-collapse").remove();
            }

            UpdateViewChilds(false);

            // MULTILINE THINGS

            if (levelConfig.AddRowMultiline) {
                const trPrincipal = rowBody.select<HTMLTableRowElement>(".principal");
                let datosMultiline = levelConfig.AddRowMultiline.OnGetDataToAddRow(dataItemT.data);

                this.AddRowMultiline(trPrincipal, datosMultiline, dataItemT, levelConfig.AddRowMultiline, colsChanged);
            }

            // STEP ROW TO CONFIG

            if (levelConfig.OnStepRowTable) {
                levelConfig.OnStepRowTable(
                    dataItemT.data,
                    trRowToDatum,
                    rowBody,
                    dataItemT.initialPos,
                    dataItemT.itemParent?.data,
                    TableUtilsGeneral.getParentsDataArray(dataItemT.itemParent));
            }
        }

        private UpdateCellView(levelConfig: ILevelEvaluator, configCol: ITableField<TDataTable>, dataItemT: TableNode<TDataTable, any>, td: TSelectionHTML<"td">, contentContainer: TSelectionHTML<"div"> | null): TSelectionHTML<"div"> {
            td.style("justify-content", configCol.Align); // FIXME Agregar al configurador css

            if (!contentContainer?.node()) {
                contentContainer = td.html(`<div class="item_cont"></div>`).select(".item_cont");
            }
            const prevField = td.property("__field");
            if (prevField && prevField !== configCol.Field) {
                prevField.split(".").forEach((strFieldName: string) => {
                    td.classed(strFieldName, false);
                });
                contentContainer.remove();
                contentContainer = td.html(`<div class="item_cont"></div>`).select(".item_cont");
            }

            td.property("__field", configCol.Field);

            //if (this.config.AddNameFieldClassToCells) {
            configCol.Field.split(".").forEach(strFieldName => {
                td.classed(strFieldName, true);
            })
            //}

            if (contentContainer.node().childElementCount == 0 || configCol.RenderCell) {
                let value = undefined;
                if (configCol.IsLowLevel) {
                    value = UIUtilGeneral._GetValueLevelsObj(configCol.Field, dataItemT.data);
                } else {
                    value = dataItemT.data[configCol.Field as any];
                }
                const fmtValue = this.CellFormater(value, configCol)
                const content: string | Node = configCol.RenderCell ? configCol.RenderCell(value, fmtValue, dataItemT.data) : fmtValue;
                if (typeof content == "string")
                    contentContainer.text(content);
                else if (content instanceof Node) {
                    contentContainer.node().textContent = "";
                    contentContainer.node().appendChild(content);
                }
            }
            if (levelConfig.OnStepCellTable) {
                levelConfig.OnStepCellTable(contentContainer, dataItemT.data, configCol.Field, dataItemT.initialPos);
            }

            if (configCol.ClassStyle) {
                contentContainer.classed(configCol.ClassStyle, true);
            }
            return contentContainer
        }

        private CellFormater(value: any, configCol: ITableField<TDataTable>): string {
            const isNumber = (configCol.Type == "number" && typeof value == "number")
            let result: string = (value == null ? "" : value) + "";
            if (isNumber) {
                if (configCol.Format == "currency")
                    result = UIUtilFormat._CurrencyFmt(value);
                else if (configCol.Format == "percent")
                    result = UIUtilFormat._PercentFmt(value);
            } else {
                const isDate = (configCol.Type == "date")
                if (isDate) {
                    let dt: Date
                    if (value instanceof Date)
                        dt = value
                    else if (typeof value == "string" || typeof value == "number")
                        dt = new Date(value)
                    let aux = UIUtilTime._DateFormatStandar(dt, configCol.Format)
                    if (aux)
                        result = aux;
                }
            }
            return result;
        }

        private DrawData(data: TableNode<TDataTable>[], tbody: TSelectionHTML<"div">, level: number, useAnimation = true) {
            tbody.selectAll<HTMLDivElement, TableNode<TDataTable>>(":scope > .tr_body")
                .interrupt()
                .data(data, (d, i) => (d.id + ""))
                .join(
                    enter => {
                        let trBody = enter.append<HTMLDivElement>("div")
                            .classed("tr_body", true)
                            .classed("row_extra", (level > 1))
                            .classed("waiting-build", true)
                            .style("min-height", "40px");

                        trBody.each((d, i, trBodyArray) => {
                            (trBodyArray[i][prop_row_stepjoin] as TStepJoin) = "enter";
                            trBodyArray[i][prop_row_updatedview] = false;
                            this.intersectedObserverRowsPrincipal.observe(trBodyArray[i]);
                        })

                        if (useAnimation) {
                            trBody
                                .style("max-height", "0px")
                                .style("overflow", "hidden")
                                .transition()
                                .duration(ANIMATION_DURATION * 2)
                                .style("max-height", "400px")
                                .on("interrupt", (d, i, trArray) => {
                                    Array.from(trArray).forEach((element) => {
                                        d3.select(element)
                                            .style("max-height", null)
                                            .style("overflow", null);
                                    })
                                })
                                .transition()
                                .duration((ANIMATION_DURATION * 2) + 10)
                                .style("max-height", null)
                                .style("overflow", null)
                                .on("interrupt", (d, i, trArray) => {
                                    Array.from(trArray).forEach((element) => {
                                        d3.select(element)
                                            .style("max-height", null)
                                            .style("overflow", null);
                                    })
                                });
                        }

                        return trBody;
                    },
                    update => {
                        return update.each((d, i, trBodyArray) => {
                            d3.select<HTMLDivElement, TableNode<TDataTable>>(trBodyArray[i])
                                .style("max-height", null)
                                .style("overflow", null);

                            (trBodyArray[i][prop_row_stepjoin] as TStepJoin) = "update";
                            trBodyArray[i][prop_row_updatedview] = false;

                            this.intersectedObserverRowsPrincipal.unobserve(trBodyArray[i]);
                            this.intersectedObserverRowsPrincipal.observe(trBodyArray[i]);
                            // this.UpdateRowData(trBody, trBody.select(".principal"), d, "update")
                        });
                    },
                    exit => {
                        this.MenuTopActiveSelectedDataOptions_Update();
                        exit.each((d, i, trBodyArray) => {
                            (trBodyArray[i][prop_row_stepjoin] as TStepJoin) = "exit";
                            trBodyArray[i][prop_row_updatedview] = false;

                            this.intersectedObserverRowsPrincipal.unobserve(trBodyArray[i]);
                            this.intersectedObserverRowsPrincipal.observe(trBodyArray[i]);
                        })

                        exit.on("interrupt", (d, i, trArray) => {
                            d3.select(trArray[i]).remove();
                        })

                        if (useAnimation) {
                            exit
                                // .style("max-height", "250px")
                                .style("max-height", (d, i, arrElem) => arrElem[i].clientHeight + "px")
                                .style("overflow", "hidden")
                                .selectAll(".tr_body")
                                .style("overflow", "hidden")

                            exit
                                .transition()
                                .duration(ANIMATION_DURATION)
                                .style("max-height", "0px")
                                .remove();
                        }
                        else {
                            exit.remove();
                        }
                    }
                )
            this.tableResizer._TableApplyScrollX();
        }

        private UpdateStickyFields() { // FIXME left de filas nivel > 1
            let headRow = this.elements.BAAA_TablaHead.select<HTMLTableRowElement>("tr")
            let cells = headRow.node().cells;
            for (let cellNode of cells) {
                if (cellNode.cellIndex != 0 && cellNode.cellIndex != cells.length - 1) {
                    let cell = d3.select(cellNode);

                    let nthStrSelChild = ":nth-child(" + (cellNode.cellIndex + 1) + ")"
                    let allFieldCells = this.elements.BA_DivSpaceTabla.selectAll("tr").selectAll<HTMLTableCellElement, any>(":scope > td" + nthStrSelChild + ", :scope > th" + nthStrSelChild)

                    if (cell.classed("sticky")) {
                        let right = 0;
                        let left = 0;
                        let isSumLeft = true;
                        for (let cellNode2 of cells) {
                            if (cellNode2 == cellNode) {
                                isSumLeft = false;
                            } else {
                                if (isSumLeft) {
                                    if (d3.select(cellNode2).classed("sticky")) {
                                        left += cellNode2.getBoundingClientRect().width
                                    }
                                } else {
                                    if (d3.select(cellNode2).classed("sticky")) {
                                        right += cellNode2.getBoundingClientRect().width
                                    }
                                }
                            }
                        }
                        // console.log("dimensiones LR: ", cellNode, left, right)
                        allFieldCells.classed("sticky", true)
                        allFieldCells.style("z-index", null)

                        allFieldCells.style("left", () => left > 0 ? left + "px" : null)

                        allFieldCells.style("right", () => right > 0 ? right + "px" : null)
                    } else {
                        allFieldCells.classed("sticky", false)
                            // .style("z-index", null)
                            .style("left", null)
                            .style("right", null)
                    }
                }
            }
        }

        private FillTableHeaderContent(th: TSelectionHTML<"th">) {
            th.append("div").classed("icon", true);
            th.append("div").classed("lbl_cont", true).append("b");
        }

        private UpdateColumnHeaderItem(datum: ITableField<TDataTable>, th: d3.Selection<HTMLTableHeaderCellElement, any, HTMLElement, any>, tr: d3.Selection<HTMLTableRowElement, any, HTMLElement, any>) {
            if (th.property("__field") && th.property("__field") != datum.Field) {
                th.classed(th.property("__field"), false);
                th.html('');
                this.FillTableHeaderContent(th);
            }
            th
                .property("__field", datum.Field)
                .classed(datum.Field, true)
                .classed("sticky", datum.Sticked)
                .style("justify-content", datum.Align)

            th.select(".lbl_cont>b")
                .text(datum.Label);

            if (this.config.EnableStickyFields || datum.IsStickable) {
                const eventSticky = (e: MouseEvent) => {
                    // if (e.target == th.select())
                    datum.Sticked = !datum.Sticked;
                    th.classed("sticky", datum.Sticked)
                    this.UpdateStickyFields()
                    e.stopPropagation()
                };

                if (th.select<HTMLElement>(".sticker").node()) {
                    th.select<HTMLElement>(".sticker").node().onclick = eventSticky;
                } else {
                    th.append("div").classed("sticker", true)
                        .append("img")
                        .attr("draggable", false)
                        .attr("src", "image/menu/ic_pin.png") // cambiar ic_pin.png por uno que represente sticker
                        .node().onclick = eventSticky;
                }
            }
            if (datum.IsSortable) {
                const eventOrder = (e: MouseEvent) => {
                    if (e.target == th.select(".sticker").node()) return
                    if (tr.classed("sizing")) return;
                    if (this.GetHasDataFiltered()) {
                        let order = CTableStatusOrder.Asc;
                        if (datum.Order === CTableStatusOrder.None) {
                            order = CTableStatusOrder.Asc;
                        } else if (datum.Order === CTableStatusOrder.Asc) {
                            order = CTableStatusOrder.Desc;
                        } else if (datum.Order === CTableStatusOrder.Desc) {
                            order = CTableStatusOrder.None;
                        }
                        this.props.ColumnHeaders.forEach(d => d.Order = CTableStatusOrder.None);
                        datum.Order = order;

                        this.UpdateDataView(true, true);
                        if (this.config.OnChangeOrder) this.config.OnChangeOrder(datum.Field, datum.Order);
                    }
                }
                th.style("cursor", null)
                if (th.select(".order_arrows").node()) {
                    th.node().onclick = eventOrder;
                } else {
                    th.append(() => this.Order_GetArrowsOrderDrawing());
                    th.node().onclick = eventOrder;
                }
            } else {
                th.style("cursor", "default")
                th.node().onclick = null
            }

            if (datum.Icon) {
                const iconD = datum.Icon;
                const c = th.select(".icon")
                const imgSrc = typeof iconD == "string" ? iconD : (iconD.Type == "img" ? iconD.Src : null);

                let imgEl = c.select<HTMLImageElement>("img").node();
                if (imgSrc && !imgEl) imgEl = c.append("img").node();
                if (imgSrc && imgEl) imgEl.src = imgSrc;
                else if (imgEl) c.select("img").remove();

                let fileEl = c.select<HTMLFileIcoElement>("wc-fileico");
                if (typeof iconD == "object" && iconD.Type == "file") {
                    if (!fileEl.node()) fileEl = c.append<HTMLFileIcoElement>("wc-fileico");
                    fileEl.attr("ext", iconD.Text)
                        .style("width", "19px")
                        .style("height", "24px")
                        .style("border", "1px solid var(--color_borderbox1)")
                        .style("border-radius", "var(--border_radius_base)")
                        // .style("margin", "3px 5px")
                        .attr("ext-color", iconD.Text.toLowerCase() == "file" ? "gray" : null)
                        .property("_LblSize", "6px");
                }
                else if (fileEl.node()) fileEl.remove();
            }
            else th.select(".icon > *").remove();

            this.Order_UpdateArrowsOrderStatus(datum.Order, th);

            if (this.config.Resizable) { this.tableResizer._AttachResizer(th, tr, datum); }
            this.tableReorderer._AttachDraggEvents(th, this.config.Reorderable);
        }

        private UpdateColumnHeaders(): void {
            let trD = this.elements.BAAA_TablaHead.select<HTMLTableRowElement>("tr");
            if (!trD.node()) {
                trD = this.elements.BAAA_TablaHead.append<HTMLTableRowElement>("tr")
            }

            let thToCheck = trD.select<HTMLTableCellElement>(".th-master-check")
            if (!thToCheck.node()) {
                thToCheck = trD.append<HTMLTableCellElement>("th")
                    .classed("th-master-check", true)
                    .classed("actioncell checkcell", true);

                thToCheck.classed("sticky", this.config.StickyCheckInRow);
                this.elements.C_CheckCol = thToCheck.append(() => this.Check_GetCheckBoxDrawing()).node();
            }

            if (!trD.select(".collapsercell").node()) {
                trD.append("th")
                    .classed("actioncell collapsercell", true)
                    // .classed("sticky", this.config.StickyCheckInRow)
                    .append("wc-ic-collapse")
                    .attr("class", "hide_transparent");
            }

            this.Check_UpdateCheckBoxStatus(this.EvaluateDataMasterCheckStatus(), this.elements.C_CheckCol);

            this.elements.C_CheckCol.onclick = (e) => {
                const datosCheckables = this.selectionManager.getAvailableCheckableNodes() // this.GetFilteredDataCheckable().reverse();

                if (datosCheckables.length) {
                    this.Check_CheckItemsData(datosCheckables, true, CTableOriginEventType.OnClickSuperCheck);
                }
                else if (this.selectionManager.hasActiveSelectedNodes()) {
                    this.Check_CheckAllData(false, CTableOriginEventType.OnClickSuperCheck);
                }
                e.stopPropagation();
            }

            trD.selectAll<HTMLTableCellElement, ITableField<TDataTable>>(".field")
                .data(this.props.ColumnHeaders, (d, i) => i + "")
                .join(
                    enter => {
                        const th = enter.append<HTMLTableCellElement>("th")
                            .style("justify-content", d => d.Align)
                            .classed("field", true);

                        this.FillTableHeaderContent(th);

                        return th.each((d, i, arrTh) => {
                            this.UpdateColumnHeaderItem(d, d3.select(arrTh[i]), trD)

                            if (d.OnLoadHead) d.OnLoadHead(d3.select(arrTh[i]));
                        });
                    },
                    update => update.each((d, i, arrTh) => {
                        this.UpdateColumnHeaderItem(d, d3.select(arrTh[i]), trD)
                    }),
                    exit => exit.remove()
                )
            // div extra para menúInRow
            const lastTH = trD.select<HTMLTableCellElement>(".th-last");
            if (!lastTH.node()) {
                trD.append<HTMLTableCellElement>("th")
                    .classed("th-last", true)
                    .style("padding", "0px")
                    .style("max-width", "0px");
            } else {
                lastTH.raise();
            }
        }

        //****************************************************************************************************
        // MENUS TOP DEFAULT
        //****************************************************************************************************

        private MenuTopDefaultOptions_GetControl() {
            if (!this.elements.C_CtrlMenuTopDefaultV2) {
                this.elements.C_CtrlMenuTopDefaultV2 = new MenuFlex({
                    Parent: this.elements.A_DivAcciones,
                    Orientation: "lefttoright",
                    OnChangeParent: (oldParent, newParent) => {
                        if (this.elements.C_CtrlMenuTopDefaultV2["__onparentchanged"]) {
                            this.elements.C_CtrlMenuTopDefaultV2["__onparentchanged"](oldParent, newParent);
                        }
                    },
                    OnShow: () => {
                        if (this.elements.C_CtrlMenuTopDefaultV2["__onshow"]) {
                            this.elements.C_CtrlMenuTopDefaultV2["__onshow"]();
                        }
                    },
                    OnHide: () => {
                        if (this.elements.C_CtrlMenuTopDefaultV2["__onhide"]) {
                            this.elements.C_CtrlMenuTopDefaultV2["__onhide"]();
                        }
                    },
                    OnGetMaxWidthCallback: () => {
                        const totalWidthAvailable = this.elements.A_DivAcciones.node().clientWidth;
                        const widthBtnSync = this.elements.A_DivAcciones.select<HTMLDivElement>(".area_sync").node()?.clientWidth || 0;
                        const widthFixed = totalWidthAvailable - widthBtnSync;
                        return widthFixed < 0 ? 0 : widthFixed;
                    },
                });
                this.elements.C_CtrlMenuTopDefaultV2._ControlContainer
                    .style("background", "var(--color_primary3)");
            }
            return this.elements.C_CtrlMenuTopDefaultV2;
        }

        private MenuTopDefaultOptions_Refresh() {
            if (!this.elements.C_CtrlMenuTopDefaultV2) {
                return null;
            }
            this.MenuTopDefaultOptions_UpdateTitle();
            return this.MenuTopDefaultOptions_UpdateOptions();
        }

        private MenuTopDefaultOptions_UpdateOptions() {
            const menuConfig = this.config.OptionsTopDefaultV2;
            const controlDefault = this.MenuTopDefaultOptions_GetControl();

            if (menuConfig?.Options.length) {
                controlDefault
                    ._SetMaxHorizontalItems(menuConfig.MaxOptionsInRow || 4)
                    ._UpdateMenuOptions(menuConfig.Options.map(d => {
                        let details = d.GetDetails ? d.GetDetails() : true;
                        let enable = (typeof details == "boolean") ? details : (details?.Enabled === undefined ? true : details.Enabled);
                        let description = (typeof details == "boolean") ? null : (details?.Description || null);

                        return {
                            Label: d.Label,
                            Enabled: enable,
                            Description: description,
                            OnClick: () => d.Callback(),
                        }
                    }))

                if (!this.elements.C_CtrlMenuTopOfActiveSelectedV2?._Visible && !controlDefault._Visible) {
                    controlDefault._Show();
                }
            } else {
                controlDefault._UpdateMenuOptions([]);
            }

            return controlDefault;
        }

        private MenuTopDefaultOptions_UpdateTitle(title?: string) {
            title = this.config.Title || ""
            const controlDefault = this.MenuTopDefaultOptions_GetControl()
                ._SetDescription(title);

            if (!this.elements.C_CtrlMenuTopOfActiveSelectedV2?._Visible && !controlDefault._Visible) {
                controlDefault._Show();
            }
            return controlDefault;
        }

        private MenuTopDefaultOptions_Visible(visible: boolean) {
            const controlDefault = this.elements.C_CtrlMenuTopDefaultV2;
            if (controlDefault) {
                if (visible && !controlDefault._Visible) {
                    controlDefault._Show();
                }
                else if (!visible && controlDefault._Visible) {
                    controlDefault._Hide();
                }
            }
        }

        //****************************************************************************************************
        // MENU TOP ACTIVE SELECTED DATA
        //****************************************************************************************************

        private MenuTopActiveSelectedDataOptions_GetControl() {
            if (!this.elements.C_CtrlMenuTopOfActiveSelectedV2) {
                // this.elements.A_DivAcciones.classed("hide", false);
                this.elements.C_CtrlMenuTopOfActiveSelectedV2 = new MenuFlex({
                    Parent: this.elements.AA_DivMenuOfChecks,
                    BtnCloseVisible: true,
                    Orientation: "lefttoright",
                    OnChangeParent: (oldParent, newParent) => {
                        if (this.elements.C_CtrlMenuTopOfActiveSelectedV2["__onparentchanged"]) {
                            this.elements.C_CtrlMenuTopOfActiveSelectedV2["__onparentchanged"](oldParent, newParent);
                        }
                    },
                    OnGetMaxWidthCallback: () => {
                        const totalWidthAvailable = this.elements.A_DivAcciones.node().clientWidth;
                        const widthBtnSync = this.elements.A_DivAcciones.select<HTMLDivElement>(".area_sync").node()?.clientWidth || 0;
                        const widthFixed = totalWidthAvailable - widthBtnSync;
                        return widthFixed < 0 ? 0 : widthFixed;
                    },
                    OnShow: () => {
                        UIUtilGlobalKeyEvents._SetEscKeyEventCallback(this.elements.C_CtrlMenuTopOfActiveSelectedV2._ControlContainer.node(), () => this.elements.C_CtrlMenuTopOfActiveSelectedV2._Hide())
                        this.MenuTopDefaultOptions_Visible(false);
                        if (this.elements.C_CtrlMenuTopOfActiveSelectedV2["__onshow"]) {
                            this.elements.C_CtrlMenuTopOfActiveSelectedV2["__onshow"]();
                        }
                    },
                    OnHide: () => {
                        this.Check_CheckAllData(false, CTableOriginEventType.OnCloseOptionsOfDataCheck);
                        UIUtilGlobalKeyEvents._RemoveEscKeyEventCallback(this.elements.C_CtrlMenuTopOfActiveSelectedV2._ControlContainer.node());
                        this.MenuTopDefaultOptions_Visible(true);
                        if (this.elements.C_CtrlMenuTopOfActiveSelectedV2["__onhide"]) {
                            this.elements.C_CtrlMenuTopOfActiveSelectedV2["__onhide"]();
                        }
                    }
                });

                this.elements.C_CtrlMenuTopOfActiveSelectedV2._ControlContainer
                    .style("background", "var(--color_primary3)");
            }
            return this.elements.C_CtrlMenuTopOfActiveSelectedV2;
        }

        private MenuTopActiveSelectedDataOptions_Refresh() {
            if (!this.elements.C_CtrlMenuTopOfActiveSelectedV2) {
                return null;
            }
            return this.MenuTopActiveSelectedDataOptions_Update();
        }

        private MenuTopActiveSelectedDataOptions_Update() {
            let fnGetMenuOptions = this.config.OptionsOfActiveSelectedDataV3;
            const controlMenuActiveSelectedData = this.MenuTopActiveSelectedDataOptions_GetControl();

            if (!fnGetMenuOptions) {
                if (controlMenuActiveSelectedData._Visible) {
                    controlMenuActiveSelectedData
                        ._UpdateMenuOptions([])
                        ._Hide();
                }
                return;
            }

            const activeSelectedData = this.selectionManager.getActiveSelectedData();

            if (activeSelectedData.length) {
                const menuConfig = fnGetMenuOptions(activeSelectedData);
                const menuFiltered = menuConfig?.Options.filter(d => (d.MultiData !== false || activeSelectedData.length <= 1)) || [];

                if (!menuConfig && controlMenuActiveSelectedData._Visible) {
                    controlMenuActiveSelectedData._Hide();
                    return;
                }
                controlMenuActiveSelectedData
                    ._SetMaxHorizontalItems(menuConfig?.MaxOptionsInRow || 4)
                    ._UpdateMenuOptions(menuFiltered.map(d => {
                        let details = d.GetDetails ? d.GetDetails(activeSelectedData) : true;
                        let enable = (typeof details == "boolean") ? details : (details?.Enabled === undefined ? true : details.Enabled);
                        let description = (typeof details == "boolean") ? null : (details?.Description || null);
                        let onMouseEnter = d.OnMouseEnter ? () => d.OnMouseEnter(activeSelectedData) : null;
                        let onMouseLeave = d.OnMouseLeave ? () => d.OnMouseLeave(activeSelectedData) : null;

                        return {
                            Label: d.Label,
                            Enabled: enable,
                            Description: description,
                            OnClick: () => d.Callback(activeSelectedData),
                            OnMouseEnter: onMouseEnter,
                            OnMouseLeave: onMouseLeave,
                        }
                    }))
                    ._SetDescription(activeSelectedData.length + " " + (
                        activeSelectedData.length == 1
                            ? UIUtilLang._GetUIString("general", "selected1")
                            : UIUtilLang._GetUIString("general", "selected2")
                    ));

                if (!controlMenuActiveSelectedData._Visible) {
                    controlMenuActiveSelectedData._Show();
                }
            }
            else if (controlMenuActiveSelectedData._Visible) {
                controlMenuActiveSelectedData._Hide();
            }
        }

        //****************************************************************************************************
        // MENU IN ROW
        //****************************************************************************************************

        private MenuRowSelectionOptions_GetControl() {
            if (!this.elements.C_CtrlMenuInRowV2) {
                this.elements.C_CtrlMenuInRowV2 = new MenuFlex({
                    ResizeReference: this.elements.BA_DivSpaceTabla,
                    Orientation: "righttoleft",
                    BtnCloseVisible: false,
                });
            }
            return this.elements.C_CtrlMenuInRowV2;
        }

        private MenuRowSelectionOptions_Remove() {
            this.elements.C_CtrlMenuInRowV2?._Hide();
        }

        private MenuRowSelectionOptions_EvalOldAndNewData(itemTable: TableNode<any>) {
            const idDataEval = itemTable.levelRow + "_" + itemTable.id;
            const idDataControl = this.MenuRowSelectionOptions_GetControl()["__idData"];

            if (idDataControl !== undefined && idDataEval === idDataControl) {
                return true;
            }
            return false;
        }

        private MenuRowSelectionOptions_Refresh() {
            if (!this.elements.C_CtrlMenuInRowV2) {
                return null;
            }
            const controlMenuRow = this.MenuRowSelectionOptions_GetControl();
            const itemTable = controlMenuRow["__data"];

            if (itemTable == null) {
                return null;
            }
            const fnGetMenuOptions = this.levelsConfig.get(itemTable?.levelRow)?.GetOptionsInRowV2;
            const menuConfig = fnGetMenuOptions ? fnGetMenuOptions(itemTable.data) : null;

            if (!menuConfig?.Options.length) {
                controlMenuRow._UpdateMenuOptions([]);
                if (controlMenuRow._Visible) {
                    return controlMenuRow._Hide();
                }
                return controlMenuRow;
            }
            return controlMenuRow
                ._SetMaxHorizontalItems(menuConfig.MaxOptionsInRow || 4)
                ._UpdateMenuOptions(menuConfig.Options.map((d) => {
                    let details = d.GetDetails ? d.GetDetails([itemTable.data]) : true;
                    let enable = (typeof details == "boolean") ? details : (details?.Enabled === undefined ? true : details.Enabled);
                    let description = (typeof details == "boolean") ? null : (details?.Description || null);
                    let onMouseEnter = d.OnMouseEnter ? () => d.OnMouseEnter([itemTable.data]) : null;
                    let onMouseLeave = d.OnMouseLeave ? () => d.OnMouseLeave([itemTable.data]) : null;

                    return {
                        Label: d.Label,
                        Enabled: enable,
                        Description: description,
                        OnClick: () => {
                            d.Callback([itemTable.data]);
                        },
                        OnMouseEnter: onMouseEnter,
                        OnMouseLeave: onMouseLeave,
                    }
                }));
        }

        private MenuRowSelectionOptions_UpdateData(itemTable: TableNode<any>, row: TSelectionHTML<"tr">) {
            if (row.select(".trorigin").node()) {
                row = row.select(".trorigin");
            }
            const childsCells = row.node().childNodes as NodeListOf<HTMLTableCellElement>;
            const controlMenuRow = this.MenuRowSelectionOptions_GetControl();

            if (this.props.ReliableCurrentData) {
                controlMenuRow["__idData"] = itemTable.levelRow + "_" + itemTable.id;
                controlMenuRow["__data"] = itemTable;
            } else {
                controlMenuRow["__idData"] = undefined;
                controlMenuRow["__data"] = undefined;
            }
            controlMenuRow
                ._SetParent(d3.select(childsCells[childsCells.length - 1]))
                ._OnParentChanged((oldParent, newParent) => {
                    if (controlMenuRow["__onparentchanged"]) {
                        controlMenuRow["__onparentchanged"](oldParent, newParent);
                    }
                })
                ._OnShow(() => {
                    row.classed("tr_click", true)
                    UIUtilGlobalKeyEvents._SetEscKeyEventCallback(this.elements.C_CtrlMenuInRowV2._ControlContainer.node(), () => this.elements.C_CtrlMenuInRowV2._Hide());

                    if (controlMenuRow["__onshow"]) {
                        controlMenuRow["__onshow"]();
                    }
                })
                ._OnHide(() => {
                    row.classed("tr_click", false);
                    UIUtilGlobalKeyEvents._RemoveEscKeyEventCallback(this.elements.C_CtrlMenuInRowV2._ControlContainer.node());
                    if (controlMenuRow["__idData"] !== undefined) {
                        controlMenuRow["__idData"] = null;
                        controlMenuRow["__data"] = null;
                    }
                    controlMenuRow
                        ._ControlContainer
                        .style("min-width", null);

                    if (controlMenuRow["__onhide"]) {
                        controlMenuRow["__onhide"]();
                    }
                })
                ._SetOnGetMaxWidthCallback(() => Util._GetMaxWidthMenuFlex(row, this.config.MenuInRowNoCellsToIgnoreWidth, this.elements.BA_DivSpaceTabla, this.MenuRowSelectionOptions_GetControl()));

            return this.MenuRowSelectionOptions_Refresh();
        }

        private MenuRowSelectionOptions_Toggle(itemTable: TableNode<any>, row: TSelectionHTML<"tr">) {
            if (row.select(".trorigin").node()) {
                row = row.select(".trorigin");
            }
            const controlMenuRow = this.MenuRowSelectionOptions_GetControl();
            if (row.classed("tr_click")) {
                controlMenuRow._Hide(); // toggle off
            }
            else if (!this.selectionManager.hasActiveSelectedNodes()) {
                if (controlMenuRow._Visible) {
                    controlMenuRow._Hide();
                }
                this.MenuRowSelectionOptions_UpdateData(itemTable, row);
                if (controlMenuRow._ItemsLength) {
                    controlMenuRow._Show();
                }
            }
        }

        //****************************************************************************************************
        // MENUS GENERAL
        //****************************************************************************************************

        private MenuControlPublicShare<Type extends ("row" | "top-active-selected" | "top-default")>(type: Type) {
            let controlMenu: MenuFlex;
            switch (type) {
                case "row":
                    controlMenu = this.MenuRowSelectionOptions_GetControl();
                    break;
                case "top-active-selected":
                    controlMenu = this.MenuTopActiveSelectedDataOptions_GetControl();
                    break;
                case "top-default":
                    controlMenu = this.MenuTopDefaultOptions_GetControl();
                    break;
            }
            type IMenuControlShare = {
                _Hide: () => IMenuControlShare;
                _Show: () => IMenuControlShare;
                _SetParent: (parent: TSelectionHTML) => IMenuControlShare;
                _SetDescription: (description: string) => IMenuControlShare;
                _Refresh: () => IMenuControlShare;
                _SetOrientation: (val: MenuFlex.TOrientation) => IMenuControlShare;
                _UpdateConfig: Type extends "top-default" ? (config: ITableMenuTopDefaultConfig) => IMenuControlShare : never;
                _SetMaxHorizontalItems: Type extends "top-default" ? never : (maxItems: number) => IMenuControlShare;
                _UpdateMenuOptions: Type extends "top-default" ? never : (options: MenuFlex.IMenuItemConfig[]) => IMenuControlShare;
                _OnHide: (callback: () => void) => IMenuControlShare;
                _OnShow: (callback: () => void) => IMenuControlShare;
                _OnParentChanged: (callback: (oldParent: TSelectionHTML, newParent: TSelectionHTML) => void) => IMenuControlShare;
                get _IsVisible(): boolean;
                get _ControlContainer(): TSelectionHTML;
            }

            const refControl: IMenuControlShare = {
                _Refresh: () => {
                    switch (type) {
                        case "top-default":
                            this.MenuTopDefaultOptions_Refresh();
                            break;
                        case "top-active-selected":
                            this.MenuTopActiveSelectedDataOptions_Refresh();
                            break;
                        case "row":
                            this.MenuRowSelectionOptions_Refresh();
                            break;
                    }
                    // controlMenu.met_Refresh();
                    return refControl;
                },
                _UpdateConfig: (type == "top-default"
                    ? (config) => {
                        this.config.OptionsTopDefaultV2 = config;
                        this.MenuTopDefaultOptions_UpdateOptions();
                        return refControl;
                    }
                    : undefined) as Type extends "top-default" ? (config: ITableMenuTopDefaultConfig) => IMenuControlShare : never,
                _SetMaxHorizontalItems: (type != "top-default"
                    ? (maxItems) => {
                        controlMenu._SetMaxHorizontalItems(maxItems);
                        return refControl;
                    }
                    : undefined) as Type extends "top-default" ? never : (maxItems: number) => IMenuControlShare,
                _UpdateMenuOptions: (type != "top-default"
                    ? (options) => {
                        controlMenu._UpdateMenuOptions(options);
                        return refControl;
                    }
                    : undefined) as Type extends "top-default" ? never : (options: MenuFlex.IMenuItemConfig[]) => IMenuControlShare,
                _Hide: () => {
                    controlMenu._Hide();
                    return refControl;
                },
                _Show: () => {
                    controlMenu._Show();
                    return refControl;
                },
                _SetParent: (parent) => {
                    controlMenu._SetParent(parent);
                    return refControl;
                },
                _SetDescription: (description: string) => {
                    controlMenu._SetDescription(description);
                    return refControl;
                },
                _SetOrientation: (value) => {
                    controlMenu._SetOrientation(value);
                    return refControl;
                },
                _OnHide: (callback: () => void) => {
                    controlMenu["__onhide"] = callback;
                    return refControl;
                },
                _OnShow: (callback: () => void) => {
                    controlMenu["__onshow"] = callback;
                    return refControl;
                },
                _OnParentChanged: (callback: (oldParent: TSelectionHTML, newParent: TSelectionHTML) => void) => {
                    controlMenu["__onparentchanged"] = callback;
                    return refControl;
                },
                _IsVisible: null,
                _ControlContainer: null,
            }

            return UIUtilGeneral._ObjectAddGetters(refControl, {
                _IsVisible: () => controlMenu._Visible,
                _ControlContainer: () => controlMenu._ControlContainer,
            });
        }

        //****************************************************************************************************
        // CHECK THINGS
        //****************************************************************************************************

        private Check_GetCheckBoxDrawing(level: number = 0) {
            const checkbox = new CheckboxComponent();
            checkbox.className = "checkbox_table";
            if (level > 1) {
                checkbox.style.marginLeft = `calc(var(--padding1) * ${level})`;
            }
            return checkbox;
        }

        private Check_UpdateCheckBoxStatus(checkStatus: Pick<TableNode, "isChecked" | "isIndeterminateChecked">, checkbox: CheckboxComponent) {
            checkbox.em_checked = checkStatus.isChecked;
            checkbox.em_indeterminate = checkStatus.isIndeterminateChecked;
        }

        private Check_CheckItemById(IDs: Array<string | number>, check: boolean) {
            const itemsFinded: TableNode<TDataTable>[] = IDs
                .filter(idSearch => this.props.DataOriginTableMap.has(idSearch))
                .map(idFind => this.props.DataOriginTableMap.get(idFind));

            if (itemsFinded.length > 0) {
                this.Check_CheckItemsData(itemsFinded, check, CTableOriginEventType.OnExternalEvent);
            }
        }

        private Check_CheckItemsData(dataItemsT: TableNode<any>[], checkValue: boolean, originEvent: CTableOriginEventType) {
            const anyChanged = this.selectionManager.toggleItemsSelection(dataItemsT, checkValue);

            // Disparamos los eventos correspondientes (OnCheckedData_): this.DispatchCheckedEvents(dataItemsT, originEvent); // NOTE: PROXIMAMENTE

            if (anyChanged && this.levelsConfig.get(1).OnActiveSelectedData) {
                const activeSelectedData = this.selectionManager.getActiveSelectedData();
                this.levelsConfig.get(1).OnActiveSelectedData(activeSelectedData, originEvent, null, null);
            }

            this.MenuRowSelectionOptions_Remove(); // Remueve menú de fila
            if (!checkValue) this.RefreshFilterMainData();
            this.MenuTopActiveSelectedDataOptions_Update();
            this.UpdateDataView(!checkValue, true);
            // Pedimos a Lit que re-renderice: this.requestUpdate(); // NOTE: PROXIMAMENTE
        }


        private Check_CheckAllData(check: boolean, originEvent: CTableOriginEventType) {
            this.Check_CheckItemsData(this.props.DataOriginTableArray, check, originEvent);
        }

        //****************************************************************************************************
        // ORDER THINGS
        //****************************************************************************************************
        //#region

        private Order_GetArrowsOrderDrawing(): TableSortArrowsComponent {
            const arrows = new TableSortArrowsComponent();
            arrows.className = "order_arrows";
            return arrows;
        }

        private Order_UpdateArrowsOrderStatus(status: CTableStatusOrder, th: TSelectionHTML<"th">) {
            const arrows: TableSortArrowsComponent = th.select<TableSortArrowsComponent>(".order_arrows").node()
            if (arrows) {
                arrows.em_status = status;
            }
        }

        private Order_GetRefreshCurrentOrder<T extends TableNode<TDataTable>>(datos: T[], level: number): T[] {
            const headerOrder = this.props.ColumnHeaders
                .find(d => (d.Order != CTableStatusOrder.None));
            const orderType = headerOrder?.Order || CTableStatusOrder.None;
            const dataSortable = datos;
            let dataResult: T[];

            switch (orderType) {
                case CTableStatusOrder.Asc:
                    if (headerOrder.OrderLevelRows == level) { // NOTE VALIDAR todo el switch a partir de una implementación de ordenamiento multiple
                        dataSortable.sort((a, b) => this.Order_EvalValueItems(headerOrder, d3.ascending, a.data, b.data));
                    }
                    break;
                case CTableStatusOrder.Desc:
                    if (headerOrder.OrderLevelRows == level) {
                        dataSortable.sort((a, b) => this.Order_EvalValueItems(headerOrder, d3.descending, a.data, b.data));
                    }
                    break;
                case CTableStatusOrder.None:
                    dataSortable.sort((a, b) => d3.ascending(a.initialPos, b.initialPos));
                    break;
            }
            if (level == 1 && this.config.ItemGrouperField) {
                dataResult = [];
                d3Group(dataSortable, d => d.data[this.config.ItemGrouperField])
                    .forEach((itemsG, key) => {
                        itemsG.forEach(d => {
                            dataResult.push(d);
                        })
                    })
            }
            else {
                dataResult = dataSortable;
            }
            return dataResult;
        }

        private Order_EvalValueItems(headerOrder: ITableField<TDataTable>, /* levelConfig: ILevelEvaluator,*/ funcToOrder: ((a: d3.Primitive, b: d3.Primitive) => number), dataA: any, dataB: any): number {
            const field = headerOrder.OrderField;
            const isLowLevel = headerOrder.OrderField.split(".").length > 1;
            const typeV = headerOrder.OrderTypeParse;

            let aValue: d3.Primitive = isLowLevel ? UIUtilGeneral._GetValueLevelsObj(field, dataA) : dataA[field];
            let bValue: d3.Primitive = isLowLevel ? UIUtilGeneral._GetValueLevelsObj(field, dataB) : dataB[field];

            switch (typeV) {
                case Date:
                    aValue = aValue ? UIUtilTime._GetValidDate(aValue as (string | Date)) : null;
                    bValue = bValue ? UIUtilTime._GetValidDate(bValue as (string | Date)) : null;
                    break;
                case String:
                    aValue = aValue ? String(aValue).toLowerCase() : "";
                    bValue = bValue ? String(bValue).toLowerCase() : "";
                    break;
                case Number:
                    aValue = aValue !== undefined ? Number(aValue) : 0;
                    bValue = bValue !== undefined ? Number(bValue) : 0;
                    break;
                case Boolean:
                    aValue = Boolean(aValue);
                    bValue = Boolean(bValue);
                    break;
                default:
                    // VALIDA: TIPOS DE DATOS GUALES
                    let ambosNumeros = (typeof aValue == "number" && typeof bValue == "number");
                    let ambosCadenas = false;
                    let ambosFechas = false;

                    if (!ambosNumeros) {
                        ambosCadenas = (typeof aValue == "string" && typeof bValue == "string");
                        if (!ambosCadenas) {
                            ambosFechas = (aValue instanceof Date && bValue instanceof Date);
                        }
                    }

                    // AJUSTA A TIPOS DE DATOS IGUALES (STRING)
                    if (!ambosNumeros && !ambosCadenas && !ambosFechas) {
                        aValue = aValue == null ? (typeof bValue == "number" ? 0 : "") : aValue;
                        bValue = bValue == null ? (typeof aValue == "number" ? 0 : "") : bValue;
                    }

                    if (typeof aValue == "string" && typeof bValue == "string") {
                        aValue = aValue.toLowerCase();
                        bValue = bValue.toLowerCase();
                        // if (!isNaN(aValue as any) && !isNaN(bValue as any)) {
                        //     aValue = Number(aValue);
                        //     bValue = Number(bValue);
                        // }
                    }
                    break;
            }

            return funcToOrder(aValue, bValue);
        }

        //#endregion

        // ********************************************************************************
        // PAGE THINGS
        // ********************************************************************************
        //#region

        // ********************************************************************************
        // SIZE THINGS
        // ********************************************************************************

        private BuildTablefFloatingOptions() {
            let floatingOptions: TItemsFloatingOptionsCfg = [];
            if (this.config.Resizable) {
                floatingOptions.push({
                    Description: _L("table.reset_dims"),
                    Enable: true,
                    OnSelect: () => this.tableResizer._ResetColumnsDimensions(),
                    Src: UIUtilIconResources.CGeneral.Dimensions,
                })
            }

            if (this.config.Reorderable) {
                floatingOptions.push({
                    Description: _L("table.reset_order"),
                    Enable: true,
                    OnSelect: () => this.tableReorderer._ResetOrder(),
                    Src: UIUtilIconResources.CGeneral.SortRepair,
                })
            }

            if (floatingOptions.length) {
                this.ctrlFloatingOptions = new FloatingOptionsBtns({
                    Items: floatingOptions,
                    Parent: this.elements.BAAA_TablaHead,
                    ElementToHover: this.elements.BAAA_TablaHead,
                    OnHide: () => {
                        if (this.#floatingOptShowTimeOut != null) {
                            clearTimeout(this.#floatingOptShowTimeOut);
                            this.#floatingOptShowTimeOut = null;
                        }
                        if (this.#floatingHideShowTimeOut != null) {
                            clearTimeout(this.#floatingHideShowTimeOut);
                            this.#floatingHideShowTimeOut = null;
                        }
                    }
                })

                this.elements.BAAA_TablaHead.node().onmouseenter = e => {
                    if (this.#floatingHideShowTimeOut != null) {
                        clearTimeout(this.#floatingHideShowTimeOut);
                        this.#floatingHideShowTimeOut = null;
                    }

                    if (this.#floatingOptShowTimeOut == null) {
                        this.#floatingOptShowTimeOut = setTimeout(() => {
                            if (!this.ctrlFloatingOptions._IsVisible()) {
                                this.ctrlFloatingOptions._Show();
                            }
                            this.#floatingOptShowTimeOut = null
                        }, 1000)
                    }
                }

                this.elements.BAAA_TablaHead.node().onmouseleave = e => {
                    if (this.#floatingOptShowTimeOut != null) {
                        clearTimeout(this.#floatingOptShowTimeOut);
                        this.#floatingOptShowTimeOut = null;
                    }

                    if (this.#floatingHideShowTimeOut == null) {
                        this.#floatingHideShowTimeOut = setTimeout(() => {
                            if (this.ctrlFloatingOptions._IsVisible()) {
                                this.ctrlFloatingOptions._Hide();
                            }
                            this.#floatingHideShowTimeOut = null;
                        }, 1000);
                    }
                }
            }
        }

        //#endregion

        // ********************************************************************************
        // PRIVATE GETs
        // ********************************************************************************
        //#region

        private GetLangContext(stringKey: string) {
            if (this.config.LangModuleKeyInContext) {
                return (UIUtilLang._GetUIString(this.config.LangModuleKeyInContext, stringKey) || stringKey);
            }
            return stringKey;
        }

        private EvaluateDataMasterCheckStatus(): Pick<TableNode, "isChecked" | "isIndeterminateChecked"> {
            const totalDataFilteredCheckeable = this.selectionManager.getAvailableCheckableNodes().length;
            const totalDataTableChecked = this.selectionManager.getCheckedNodes().length;
            const totalDataFilteredIndeterminate = this.selectionManager.getIndeterminateNodes().length;
            return {
                isChecked: totalDataTableChecked > 0 && totalDataFilteredCheckeable == 0,
                isIndeterminateChecked: (totalDataTableChecked > 0 && totalDataFilteredCheckeable > 0) || totalDataFilteredIndeterminate > 0
            };
        }

        private GetHasDataFiltered(): boolean {
            return this.props.DataTableFilteredMap.size > 0;
        }

        private GetDataViewArray(datos: TableNode<TDataTable, any>[]): TDataAsInnerText<TDataTable>[] {
            const container = !this.elements.BAAB_TablaBodyA.classed("hide") ? this.elements.BAAB_TablaBodyA : this.elements.BAAB_TablaBodyB
            const datosFinal: TDataAsInnerText<TDataTable>[] = []
            datos.forEach(dataItemT => {
                const levelConfig = this.levelsConfig.get(dataItemT.levelRow);
                const dataView = <TDataAsInnerText<TDataTable>>{}
                const rowBody = container.append("div").attr("class", "tr_body")
                const trRowToDatum: TSelectionHTML<"tr"> = this.BuildRowAndCells(rowBody, dataItemT.levelRow);
                const tds = (() => {
                    if (trRowToDatum.node().firstElementChild instanceof HTMLTableCellElement) {
                        return trRowToDatum.selectAll<HTMLTableCellElement, any>(":scope>.field").nodes(); //trRow.node().cells
                    } else {
                        return trRowToDatum.selectAll<HTMLTableCellElement, any>(":scope>.trorigin>.field").nodes(); //trRow.node().cells
                    }
                })()
                this.props.ColumnHeaders.forEach((columnConfig, i) => {
                    const td = d3.select(tds[i])
                    const contentContainer = this.UpdateCellView(levelConfig, columnConfig, dataItemT, td, null)
                    dataView[columnConfig.Field] = contentContainer.node().innerText;
                })
                datosFinal.push(dataView)
                rowBody.remove()
            })
            return datosFinal
        }

        /**
         * Retorna un objeto con la información del dato y sus childs relacionados
         * al estado de selección activa (si el dato tiene selección activa),
         * si no tiene selección activa retorna null.
         */
        private GetActiveSelectedDetailRowData(itemT: TableNode<any>): IActiveSelectedDataAdvanced<any> | null {
            if (!itemT.hasActiveSelection) {
                return null;
            }

            return <IActiveSelectedDataAdvanced<TDataTable>>{
                Id: itemT.id,
                Data: itemT.data,
                IsChecked: itemT.isChecked,
                IsEnabled: itemT.isRowEnable,
                IsCollapsed: itemT.isCollapsed,
                IsIndeterminateChecked: itemT.isIndeterminateChecked,
                HasActiveSelection: itemT.hasActiveSelection,
                ActiveChildSelections: itemT.listActiveSelectedChildNodes
                    .map(d => this.GetActiveSelectedDetailRowData(d))
            }
        }

        /** Retorna todos los datos con selección activa, incluye childs */
        private GetActiveSelectedItemsWithChildren() {
            return this.selectionManager.getActiveSelectedNodes().map(d => {
                return this.GetActiveSelectedDetailRowData(d);
            })
        }

        /** Retorna información y métodos relacionados con los estados del dato encontrado */
        private GetRowController<TData, TDataChild>(
            id: TTableIdType,
            collectionData: Map<TTableIdType, TableNode<TData>>,
            itemFinded_?: TableNode<TData>
        ): IRowController<TData, TDataChild> {

            const itemFinded = itemFinded_ ? itemFinded_ : collectionData.get(id);
            const selectItem: IRowController<TData, TDataChild> = {
                Id: itemFinded?.id,
                Data: itemFinded?.data,
                IsChecked: (itemFinded ? itemFinded.isChecked : false),
                IsIndeterminateChecked: (itemFinded ? itemFinded.isIndeterminateChecked : false),
                HasActiveSelected: (itemFinded ? itemFinded.hasActiveSelection : false),
                IsEnable: (itemFinded ? itemFinded.isRowEnable : false),
                IsCollapsed: (itemFinded ? itemFinded.isCollapsed : true),
                // ChildrenData: (itemFinded ? new Map(Array.from(itemFinded.dataCollapser.values()).map(d => ([d.id, this.GetSelectItem<TDataChild, any>(undefined, undefined, d)]))) : new Map()),
                GetChildrenData: () => (itemFinded ? new Map(Array.from(itemFinded.childDataOriginMap.values()).map(d => ([d.id, this.GetRowController<TDataChild, any>(undefined, undefined, d)]))) : new Map()),
                GetChildrenDataFiltered: () => (itemFinded ? new Map(itemFinded.childDataFilteredArray.map(d => ([d.id, this.GetRowController<TDataChild, any>(undefined, undefined, d)]))) : new Map()),
                SelectChildItem: (id) => {
                    return this.GetRowController<any, any>(id, ((itemFinded && itemFinded.childDataOriginMap) ? itemFinded.childDataOriginMap : new Map()));
                },
                CheckItem: (check) => {
                    if (itemFinded) {
                        this.selectionManager.toggleItemsSelection([itemFinded], check);
                    }
                    return selectItem;
                },
                EnableItemRow: (enable) => {
                    if (itemFinded) itemFinded.isRowEnable = enable;
                    return selectItem;
                },
                CollapseItem: (collapse: boolean) => {
                    if (itemFinded) itemFinded.isCollapsed = collapse;
                    return selectItem;
                },
                // RemoveFromTable: () => {
                //     collectionData.delete(id);
                //     return item;
                // },
                RefreshTableView: () => {
                    this.UpdateDataView();
                    return selectItem;
                }
            }
            return selectItem;
        }

        private GetRGBColorToLevelRow(level: number) {
            let backgroundColor = 255 - ((level - 1) * 10);
            return `rgb(${backgroundColor}, ${backgroundColor}, ${backgroundColor})`;
        }

        //#endregion

        // ********************************************************************************
        // PUBLIC PROPERTIES
        // ********************************************************************************

        //#region

        get _Control() {
            return this.elements.DivContent;
        }

        /** Menú ubicado en la parte superior de la tabla, con las opciones configuradas como por default */
        get _MenuTopDefault() {
            return this.MenuControlPublicShare("top-default");
        }

        /** Menú ubicado en la parte superior de la tabla, cada vez que hay datos activos seleccionados (sólo si está incluido en la configuración) */
        get _MenuTopOfActiveSelectedData() {
            return this.MenuControlPublicShare("top-active-selected");
        }

        /** Menú ubicado en la fila clickeada siempre que no haya ningún dato checkeado (el menú es visible e invisible con cada click) */
        get _MenuInRow() {
            return this.MenuControlPublicShare("row");
        }

        get _dataChecked_(): TDataTable[] {
            return this.selectionManager.getCheckedData();
        }
        get _getActiveSelectedData(): TDataTable[] {
            return this.selectionManager.getActiveSelectedData();
        }
        get _hasActiveSelectedData(): boolean {
            return this.selectionManager.hasActiveSelectedNodes();
        }
        get _activeSelectedItemsWithChildren() {
            return this.GetActiveSelectedItemsWithChildren();
        }

        get _data() {
            return this.props.DataOrigin;
        }

        get _dataFiltered() {
            return this.props.DataTableFilteredArray.map(d => d.data);
        }

        /** Retorna en un arreglo el texto de cada columna con el valor de `innerText` de la celda correspondiente por dato */
        get _SelectedCellsTextValues(): TDataAsInnerText<TDataTable>[] {
            const dataToInnerText = this.selectionManager.getActiveSelectedNodes();
            return this.GetDataViewArray(dataToInnerText)
        }

        /** Retorna en un arreglo el texto de cada columna con el valor de `innerText` de la celda correspondiente por dato */
        get _DataFilteredInnerText(): TDataAsInnerText<TDataTable>[] {
            const dataToInnerText = this.props.DataTableFilteredArray
            return this.GetDataViewArray(dataToInnerText)
        }

        get _InfoColumns(): TInfoColumns<TDataTable> {
            return this.config.Columns || this.config.RenderColumnHeadings;
        }

        get _ColumnsHeaders() { return this.props.ColumnHeaders; }

        get _CurrentSearchText(): string {
            return this.elements.C_CtrlFiltrosV2?._CurrentSearchText || "";
        }

        get _CurrentOrder() {
            let order = this.props.ColumnHeaders
                .find(d => (d.Order != CTableStatusOrder.None));
            return {
                Field: order?.Field,
                Type: order?.Order,
            }
        }

        get _ColumnsDimConfig() {
            return this.props.ColumnHeaders.map(d => ({ Field: d.Field, Width: d.Width, MinWidth: d.MinWidth, MaxWidth: d.MaxWidth }))
        }

        get _BAAA_TableHead() { return this.elements.BAAA_TablaHead; }

        get _BAAA_TableBodyA() { return this.elements.BAAB_TablaBodyA; }

        get _BAAA_TableBodyB() { return this.elements.BAAB_TablaBodyB; }

        get _BA_DivSpaceTabla() { return this.elements.BA_DivSpaceTabla; }

        get _BA_SpaceTablaStyleSizing() { return this.elements.BA_SpaceTablaStyleSizing; }

        get _BA_SpaceTablaSwitchLastCellSizing() { return this.elements.BA_SpaceTablaSwitchLastCellSizing; }

        get _TableOrderCfgLS() { return this.GetTableObjectConfig_LS("tableorderconfig"); }

        get _TableDimCfgLS() { return this.GetTableObjectConfig_LS("tabledimconfig"); }

        get _TableId() { return this.config.IdTabla; }

        get _TableIdentifier() { return this.tableIdentifier; }

        get _ConfigColumnsHeaders() {
            return (this.config.Columns || this.config.RenderColumnHeadings as TTableColumnConfigV2<TDataTable>[])
                .map<ITableField<TDataTable> & { Type?: any }>((item, i, arr) => {
                    let ObjOrder = <ITableField<TDataTable>>{
                        Order: CTableStatusOrder.None,
                        Sticked: item.Sticked,
                        IsLowLevel: item.Field.split(".").length > 1,
                        OriginalMinWidth: item.MinWidth
                    };
                    item.IsSortable = item.IsSortable === undefined || item.IsSortable === null ? true : item.IsSortable;
                    item.IsStickable = item.IsStickable === undefined || item.IsStickable === null ? false : item.IsStickable;
                    item.Sticked = item.Sticked === undefined || item.Sticked === null ? false : item.Sticked;
                    item.Type = item.Type || "text";

                    if (!item.Align && ["number", "date"].includes(item.Type))
                        item.Align = "end";
                    else if (!item.Align && ["img", "file"].includes(item.Type))
                        item.Align = "center";

                    if (this.config.OrderDefault && this.config.OrderDefault.Field == item.Field) {
                        ObjOrder.Order = this.config.OrderDefault.Type;
                    }
                    item.OrderLevelRows = item.OrderLevelRows || 1;
                    item.OrderTypeParse = item.OrderTypeParse || "auto";
                    item.OrderField = item.OrderField || item.Field;

                    if (item.LabelLangKey === undefined) {
                        item.LabelLangKey = item.Field;
                    }

                    if (item.LabelLangKey) {
                        let lblTag = this.GetLangContext(item.LabelLangKey);
                        if (lblTag != item.LabelLangKey) {
                            item.Label = lblTag;
                        }
                    }

                    return { ...item, ...ObjOrder }
                });
        }

        get _ConfigMinWidth() { return this.config.MinWidth; }

        get _IsColumnsResizable() { return this.config.Resizable; }
        //#endregion

        // ********************************************************************************
        // PUBLIC METHODS
        // ********************************************************************************

        //#region

        public _UpdateData(data: Array<TDataTable>) {
            this.UpdateData(data);
            return this;
        }

        public _RefreshFilter() {
            this.RefreshFilterMainData();
            this.UpdateDataView();
            return this;
        }

        public _SetSearchText(value: string) {
            this.elements.C_CtrlFiltrosV2._SetSearchText(value);
            return this;
        }

        public _UpdateTitle(title: string) {
            this.config.Title = title;
            this.MenuTopDefaultOptions_UpdateTitle(title);
            return this;
        }

        public _HideCheckboxes(hide: boolean) {
            this.config.HideCheckboxes = hide;
            this.elements.BA_DivSpaceTabla.classed("without_checkboxes", hide);
            this.UpdateDataView();
            return this;
        }

        /** Refresca los elementos UI de la tabla
        * @param doOnEndUpdateDataInView ayuda a evitar bucles en caso de invocar un refres de table desde this.config.OnEndUpdateDataInView
        */
        public _RefreshView(firstAreaToUpdate: "A" | "B" = "A", doOnEndUpdateDataInView = true) {
            this.UpdateDataView(firstAreaToUpdate == "A", false, doOnEndUpdateDataInView);
            return this;
        }

        /** Checkea todos los items encontrados incluyendo los desplegables (si existen) */
        public _CheckItemsByID(IDs: (Array<TTableIdType> | TTableIdType), check: boolean) {
            this.Check_CheckItemById((Array.isArray(IDs) ? IDs : [IDs]), check);
            return this;
        }

        /** Checkea todos los items incluyendo los desplegables (si existen) */
        public _CheckAllItems(check: boolean) {
            this.Check_CheckAllData(check, CTableOriginEventType.OnExternalEvent);
            return this;
        }

        /** Retorma una selección de un item de la tabla comenzando por los datos del nivel 1 */
        public _GetRowController<TDataChild>(id: TTableIdType): IRowController<TDataTable, TDataChild> {
            return this.GetRowController<TDataTable, TDataChild>(id, this.props.DataTableFilteredMap);
        }

        public _SetColumns(columns: IColumn<TDataTable>[]) {
            if (this.config.Columns)
                this.config.Columns = columns;
            else
                this.config.RenderColumnHeadings = columns;
            this.BuildColumnsConfig();
            this.UpdateDataView();
            this.tableResizer._BuildSizeStylesConfig();
            return this;
        }

        public _RefreshTableColumnsHeaders() {
            this.UpdateColumnHeaders();
        }

        public _SetColumnHeaders(columns: ITableField<TDataTable>[]) {
            this.props.ColumnHeaders = columns;
        }

        public _UpdateDataView(orderData = false) {
            this.UpdateDataView(true, orderData, false);
        }

        public _SetIdTable(idTabla: string) {
            this.config.IdTabla = idTabla;
        }

        public _SetMainFilters(filters: TTableParametroFiltro<TDataTable>[]) {
            this.config.FilterParameters = filters;
            this.BuildFilters();
            return this;
        }

        //#endregion

        // ********************************************************************************
        // EXTRA FEATURES
        // ********************************************************************************

        private AddRowMultiline<TData>(trPrincipal: TSelectionHTML<"tr">, datos: any[], datoParent: TableNode<any>, config: ITableRowMultilineConfig<any, any>, resetView = false) {
            let templateTr = "";
            let columns = this.props.ColumnHeaders;

            if (trPrincipal.select(".trorigin").node()) {
                templateTr = trPrincipal.select(".trorigin").html();
            } else {
                templateTr = trPrincipal.html();

                let tds = trPrincipal
                    .style("flex-direction", "column")
                    .selectAll("td");
                tds.remove();
                let originalTr = trPrincipal.append("tr")
                    .classed("trorigin", true)
                    .style("border-bottom", "none");

                tds.each((d, i, arrElem) => {
                    originalTr.append(() => arrElem[i])
                })
            }

            let updateTr = (tr: TSelectionHTML<"tr">, datum: TData, step: TStepJoin) => {
                let tds = tr.selectAll<HTMLTableCellElement, any>(".field").nodes();
                let isEnable = true;
                let parents = [];
                if (config.OnStepCell || config.OnStepNewTrs) {
                    parents.push(...TableUtilsGeneral.getParentsDataArray(datoParent));
                }
                columns.forEach((d, i) => {
                    let td = d3.select(tds[i])
                    const prevField = td.property("__field");
                    if (prevField && prevField !== d.Field) {
                        prevField.split(".").forEach((strFieldName: string) => {
                            td.classed(strFieldName, false);
                        });
                        td.classed(d.Field, true);
                    }

                    td.property("__field", d.Field);
                    let content = td.html(`<div class="item_cont"></div>`).select<HTMLDivElement>(".item_cont"); // FIXME
                    // let content = td.select<HTMLDivElement>(".item_cont").text("");
                    // if (!content.node()) {
                    //     content = td.append("div").classed("item_cont", true);
                    // }
                    if (config.OnStepCell) {
                        config.OnStepCell(content, datum, d.Field, datoParent.data, parents);
                    }
                })
                if (config.OnStepNewTrs) {
                    isEnable = config.OnStepNewTrs(datum, tr, datoParent.data, parents);
                }
                tr.classed("tr_disabled", !isEnable);
            }

            if (resetView) {
                trPrincipal.selectAll<HTMLTableRowElement, TData>(":scope > .trcopy").remove();
            }
            trPrincipal.selectAll<HTMLTableRowElement, TData>(":scope > .trcopy").data(datos).join(
                enter => {
                    let tr = enter.append("tr")
                        .classed("trcopy", true)
                        .html(templateTr)
                        .style("border-bottom", "none");

                    tr.select(".lastcell .menuflex").remove();

                    tr.selectAll(".actioncell").select(".checkbox_table").classed("hide_transparent", true);

                    tr.selectAll(":scope > td:not(.lastcell)")
                        .style("padding-top", "5px", "important")
                        .style("padding-bottom", "5px")

                    tr.each((d, i, arrElem) => {
                        updateTr(d3.select(arrElem[i]), d, "enter");
                        return false;
                    })
                    return tr
                },
                update => update.each((d, i, arrElem) => {
                    updateTr(d3.select(arrElem[i]), d, "update");
                    return false;
                }),
                exit => exit.each((d, i, arrElem) => {
                    updateTr(d3.select(arrElem[i]), d, "exit");
                    return false;
                }).remove()
            )
        }
    }
}